import { friendlyError } from '../../../utils/userMessages';
import { resolveCompanyId } from '../../../utils/companyIdentity';
import { brandPalette as palette, getContrastText, getReadableColor, getGradientContrastText } from '../../../utils/brandPalette';
// src/components/dashboard/CompanyTeam/CompanyTeam.jsx
// ============================================================================
// TelcoFair — Company Team Management
// Aesthetic: "Frosted Signal" — glass engine with overlay icons
// Auto-admin for company creator, manual user creation with email verification
// ============================================================================

import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    Grid,
    TextField,
    Button,
    Switch,
    FormControlLabel,
    Alert,
    Card,
    CardContent,
    Avatar,
    IconButton,
    Chip,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    CircularProgress,
    Divider,
    Stack,
    Badge,
    Menu,
    Fade,
    Collapse,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    ListItemSecondaryAction,
    Skeleton,
    Tabs,
    Tab,
    LinearProgress,
    Popover,
    alpha,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Block as BlockIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Refresh as RefreshIcon,
    People as PeopleIcon,
    Person as PersonIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    Lock as LockIcon,
    Shield as ShieldIcon,
    Verified as VerifiedIcon,
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    AdminPanelSettings as AdminIcon,
    AdminPanelSettings as ManagerIcon,
    PersonAdd as PersonAddIcon,
    VisibilityOff as VisibilityOffIcon,
    BadgeOutlined as WorkIcon,
    PersonAdd as GroupAddIcon,
    FiberManualRecord as LiveIcon,
    TrendingUp as TrendingUpIcon,
    Close as ClearIcon,
    PhotoCamera as PhotoCameraIcon,
    Menu as MoreVertIcon,
    Business as BusinessIcon,
    LocationOn as LocationIcon,
    Public as PublicIcon,
    Link as LinkIcon,
    Close as CloseIcon,
    Check as CheckIcon,
    PeopleAlt as PeopleAltIcon,
    Inventory2 as CategoryIcon,
    Add as AddCircleIcon,
    Cancel as RemoveCircleIcon,
    CalendarToday as CalendarIcon,
    Schedule as AccessTimeIcon,
    Analytics as AnalyticsIcon,
    Info as InfoIcon,
    Chat as ChatIcon,
    Send as SendIcon,
    Image as ImageIcon,
    Pending as PendingIcon,
    Flag as FlagIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Group as GroupIcon,
    Restore as HistoryIcon,
    NetworkCheck as WifiIcon,
    NetworkCheck as CellTowerIcon,
    NetworkCheck as RouterIcon,
    SettingsApplications as HubIcon,
    Speed as SpeedIcon,
    Cloud as CloudIcon,
    NetworkCheck as SignalBarsIcon,
    PaidOutlined as PaymentIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../auth/AuthContext';
import { db } from '../../../config/firebase';
import {
    collection,
    getDocs,
    getDoc,
    doc,
    updateDoc,
    setDoc,
    serverTimestamp,
    query,
    orderBy,
    where,
    arrayUnion,
    arrayRemove,
    runTransaction
} from 'firebase/firestore';
import { updatePassword, deleteUser } from 'firebase/auth';
import toast from 'react-hot-toast';
import Header from '../../common/Header/Header';
import LoadingSpinner from '../../common/LoadingSpinner';
import { uploadFile } from '../../../services/integrations/storage';
import { createCompanyMember, updateCompanyMember, removeCompanyMember, setCompanyMemberStatus, getCompanySeatStatus, markExtraSeatPaymentPaid } from '../../../services/integrations/backend';

// ============================================
// PALETTE
// ============================================



const SHADOW = '25, 57, 78';
const EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
const BOUNCE = 'cubic-bezier(0.34, 1.56, 0.64, 1)';

// ============================================================================
// ELEVATION LADDER
// ============================================================================

const ELEVATION = {
    0: { ambY: 2,  ambB: 8,   ambA: 0.05, conY: 1,  conB: 2,  conA: 0.04, blur: 8,  rake: 0.5,  fill: 0.34 },
    1: { ambY: 8,  ambB: 24,  ambA: 0.09, conY: 2,  conB: 6,  conA: 0.05, blur: 14, rake: 0.68, fill: 0.5 },
    2: { ambY: 16, ambB: 44,  ambA: 0.12, conY: 4,  conB: 12, conA: 0.07, blur: 20, rake: 0.82, fill: 0.62 },
    3: { ambY: 28, ambB: 70,  ambA: 0.15, conY: 7,  conB: 20, conA: 0.08, blur: 26, rake: 0.92, fill: 0.7 },
    4: { ambY: 42, ambB: 104, ambA: 0.18, conY: 11, conB: 30, conA: 0.09, blur: 32, rake: 1,    fill: 0.78 },
};

// ============================================================================
// GLASS STYLES
// ============================================================================

const glassCard = ({ level = 2, radius = '14px', tint = palette.paper, accent = null } = {}) => ({
    position: 'relative',
    overflow: 'hidden',
    borderRadius: radius,
    background: accent
        ? `radial-gradient(circle at 100% 0%, ${alpha(accent,.075)}, transparent 34%), linear-gradient(145deg, #FFFFFF 0%, ${alpha(accent,.025)} 100%)`
        : 'linear-gradient(145deg, #FFFFFF 0%, #F9FCFD 100%)',
    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.075)}`,
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : level === 0
            ? '0 2px 7px rgba(25,57,78,.035)'
            : '0 5px 18px rgba(25,57,78,.055)',
    transition: 'transform .2s ease, box-shadow .2s ease, border-color .2s ease',
});

const glassLift = (level = 3, accent = palette.rust) => ({
    transform: 'translateY(-2px)',
    borderColor: alpha(accent, 0.14),
    boxShadow: level >= 3
        ? '0 12px 30px rgba(25,57,78,.09)'
        : '0 8px 20px rgba(25,57,78,.07)',
});

const glassWell = (radius = '10px', accent = null) => ({
    borderRadius: radius,
    background: accent ? alpha(accent, 0.045) : '#F4F8FB',
    border: `1px solid ${accent ? alpha(accent, 0.10) : alpha(palette.ink, 0.065)}`,
    boxShadow: 'none',
});

const glassPod = (color, size = 38, radius = '10px') => ({
    width: size,
    height: size,
    borderRadius: radius,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    color: getContrastText(color),
    background: `linear-gradient(145deg, ${color}, ${alpha(color, .72)})`,
    border: '1px solid rgba(255,255,255,.68)',
    boxShadow: `0 7px 16px ${alpha(color,.22)}, inset 0 1px 0 rgba(255,255,255,.48)`,
});

const glassPill = (color, filled = false) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    minHeight: 22,
    borderRadius: '999px',
    fontSize: '10.5px',
    lineHeight: 1,
    fontWeight: 650,
    whiteSpace: 'nowrap',
    color: filled ? getContrastText(color) : getReadableColor(color),
    background: filled ? color : alpha(color, .07),
    border: `1px solid ${filled ? alpha(color, .12) : alpha(color, .10)}`,
    boxShadow: 'none',
});

const glassButton = (color, deep) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    color: getGradientContrastText(color, deep || color),
    minHeight: 34,
    paddingInline: 12,
    background: `linear-gradient(135deg, ${color}, ${deep || color})`,
    border: `1px solid ${alpha(color, .12)}`,
    boxShadow: `0 5px 14px ${alpha(color, .16)}`,
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-1px)', boxShadow: `0 8px 18px ${alpha(color, .20)}` },
    '&.Mui-disabled': { opacity: .58, color: '#fff' },
});

const glassGhostButton = (color) => ({
    borderRadius: '9px',
    textTransform: 'none',
    fontWeight: 650,
    minHeight: 34,
    color: getReadableColor(color),
    background: 'rgba(255,253,250,.92)',
    border: `1px solid ${alpha(color, .15)}`,
    boxShadow: '0 2px 7px rgba(25,57,78,.03)',
    transition: 'background .18s ease, border-color .18s ease',
    '&:hover': { background: alpha(color, .035), borderColor: alpha(color, .22) },
});

// ============================================================================
// GLOBAL CSS
// ============================================================================

const GLOBAL_CSS = `
    @keyframes tfAuroraA {
        0%, 100% { transform: translate3d(0,0,0) scale(1);           opacity: 0.55; }
        33%      { transform: translate3d(60px,-40px,0) scale(1.18); opacity: 0.85; }
        66%      { transform: translate3d(-40px,30px,0) scale(0.92); opacity: 0.5; }
    }
    @keyframes tfAuroraB {
        0%, 100% { transform: translate3d(0,0,0) scale(1.05);        opacity: 0.5; }
        40%      { transform: translate3d(-70px,45px,0) scale(0.88); opacity: 0.8; }
        75%      { transform: translate3d(35px,-30px,0) scale(1.2);  opacity: 0.62; }
    }
    @keyframes tfAuroraC {
        0%, 100% { transform: translate3d(0,0,0) scale(0.95);        opacity: 0.6; }
        50%      { transform: translate3d(50px,55px,0) scale(1.25);  opacity: 0.85; }
    }
    @keyframes tfRingSpin    { from { transform: rotate(0deg); }   to { transform: rotate(360deg); } }
    @keyframes tfRingSpinRev { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
    @keyframes tfNodeRise {
        0%   { transform: translate3d(0,0,0) scale(0.6);        opacity: 0; }
        12%  { opacity: 0.8; }
        88%  { opacity: 0.4; }
        100% { transform: translate3d(26px,-104vh,0) scale(1.1); opacity: 0; }
    }
    @keyframes tfScanSweep {
        0%   { transform: translate3d(0,-14vh,0); opacity: 0; }
        8%   { opacity: 1; }
        92%  { opacity: 1; }
        100% { transform: translate3d(0,114vh,0); opacity: 0; }
    }
    @keyframes tfSignalWave {
        0%   { transform: scale(0.6); opacity: 0.8; }
        70%  { opacity: 0.12; }
        100% { transform: scale(2.2); opacity: 0; }
    }
    @keyframes tfBreathe {
        0%, 100% { transform: scale(1); }
        50%      { transform: scale(1.06); }
    }
    @keyframes tfSheen {
        0%   { transform: translateX(-130%) skewX(-18deg); }
        100% { transform: translateX(320%)  skewX(-18deg); }
    }
    @keyframes tfFloatIcon {
        0%, 100% { transform: translate3d(0,0,0) rotate(0deg);     opacity: 0.09; }
        50%      { transform: translate3d(0,-16px,0) rotate(5deg); opacity: 0.18; }
    }
    @keyframes tfGrain {
        0%   { transform: translate3d(0,0,0); }
        20%  { transform: translate3d(-2%,2%,0); }
        40%  { transform: translate3d(2%,-1%,0); }
        60%  { transform: translate3d(-1%,-2%,0); }
        80%  { transform: translate3d(1%,1%,0); }
        100% { transform: translate3d(0,0,0); }
    }
    @keyframes tfEdgeShift {
        0%   { background-position: 0% 0%; }
        100% { background-position: 300% 0%; }
    }
    @keyframes tfSkeleton {
        0%   { transform: translateX(-120%); }
        100% { transform: translateX(320%); }
    }
    @keyframes tfPulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50%      { opacity: 0.5; transform: scale(0.8); }
    }

    @media (prefers-reduced-motion: reduce) {
        *, *::before, *::after {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001ms !important;
        }
    }
`;

const FONT_HREF =
    'https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700' +
    '&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap';

let assetsInjected = false;
const injectAssets = () => {
    if (assetsInjected || typeof document === 'undefined') return;
    assetsInjected = true;
    const link = (rel, href, cross) => {
        const el = document.createElement('link');
        el.rel = rel;
        el.href = href;
        if (cross) el.crossOrigin = 'anonymous';
        document.head.appendChild(el);
    };
    link('preconnect', 'https://fonts.googleapis.com');
    link('preconnect', 'https://fonts.gstatic.com', true);
    link('stylesheet', FONT_HREF);

    const style = document.createElement('style');
    style.setAttribute('data-tf', 'team');
    style.textContent = GLOBAL_CSS;
    document.head.appendChild(style);
};

const prefersReducedMotion = () =>
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ============================================================================
// AURORA BACKDROP
// ============================================================================

const CLOUDS = [
    { color: palette.rust,   size: 620, top: '-16%', left: '-8%',  dur: 34, delay: 0,   anim: 'tfAuroraA', depth: 26 },
    { color: palette.pine,   size: 520, top: '8%',   left: '68%',  dur: 42, delay: 3,   anim: 'tfAuroraB', depth: -18 },
    { color: palette.gold,   size: 440, top: '56%',  left: '58%',  dur: 46, delay: 5,   anim: 'tfAuroraA', depth: 22 },
    { color: palette.teal,   size: 400, top: '44%',  left: '-12%', dur: 30, delay: 1.5, anim: 'tfAuroraC', depth: -14 },
    { color: palette.purple, size: 360, top: '24%',  left: '34%',  dur: 36, delay: 2.5, anim: 'tfAuroraB', depth: 10 },
];

const GLYPHS = [
    { Icon: WifiIcon,       x: '6%',  y: '20%', size: 28, dur: 6,   delay: 0 },
    { Icon: CellTowerIcon,  x: '90%', y: '12%', size: 32, dur: 7.5, delay: 0.5 },
    { Icon: RouterIcon,     x: '11%', y: '74%', size: 24, dur: 6.5, delay: 1 },
    { Icon: HubIcon,        x: '84%', y: '68%', size: 26, dur: 8,   delay: 1.5 },
    { Icon: SpeedIcon,      x: '44%', y: '7%',  size: 22, dur: 7,   delay: 2 },
    { Icon: CloudIcon,      x: '74%', y: '90%', size: 30, dur: 6.8, delay: 2.5 },
    { Icon: SignalBarsIcon, x: '94%', y: '44%', size: 24, dur: 6.2, delay: 3.5 },
];

const NODES = [
    { s: 6, l: '12%', t: '94%', d: 19, delay: 0,   c: palette.rust },
    { s: 4, l: '26%', t: '97%', d: 24, delay: 3,   c: palette.teal },
    { s: 7, l: '42%', t: '92%', d: 21, delay: 6,   c: palette.rust },
    { s: 5, l: '57%', t: '99%', d: 27, delay: 1.5, c: palette.pine },
    { s: 4, l: '70%', t: '95%', d: 23, delay: 8,   c: palette.gold },
    { s: 6, l: '85%', t: '91%', d: 30, delay: 4.5, c: palette.teal },
    { s: 5, l: '34%', t: '98%', d: 26, delay: 11,  c: palette.purple },
    { s: 4, l: '95%', t: '96%', d: 22, delay: 7,   c: palette.pine },
];

const BroadcastRings = memo(({ size, opacity, reverse }) => (
    <Box component="svg" viewBox="0 0 400 400" sx={{ width: size, height: size, opacity, overflow: 'visible' }}>
        <g fill="none">
            <circle cx="200" cy="200" r="80" stroke={palette.rust} strokeWidth="1" strokeDasharray="3 10"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpinRev' : 'tfRingSpin'} 48s linear infinite` }} />
            <circle cx="200" cy="200" r="132" stroke={palette.gold} strokeWidth="1" strokeDasharray="1 14"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpin' : 'tfRingSpinRev'} 64s linear infinite` }} />
            <circle cx="200" cy="200" r="186" stroke={palette.teal} strokeWidth="1" strokeDasharray="6 18"
                    style={{ transformOrigin: '200px 200px', animation: `${reverse ? 'tfRingSpinRev' : 'tfRingSpin'} 86s linear infinite` }} />
        </g>
    </Box>
));
BroadcastRings.displayName = 'BroadcastRings';

const AuroraBackdrop = memo(() => {
    const rootRef = useRef(null);

    useEffect(() => {
        if (typeof window === 'undefined' || prefersReducedMotion()) return undefined;
        const el = rootRef.current;
        if (!el) return undefined;

        let ticking = false;
        let mx = 0;
        let my = 0;

        const apply = () => {
            ticking = false;
            el.style.setProperty('--mx', mx.toFixed(4));
            el.style.setProperty('--my', my.toFixed(4));
        };
        const onMove = (e) => {
            mx = e.clientX / window.innerWidth - 0.5;
            my = e.clientY / window.innerHeight - 0.5;
            if (!ticking) { ticking = true; requestAnimationFrame(apply); }
        };

        window.addEventListener('mousemove', onMove, { passive: true });
        return () => window.removeEventListener('mousemove', onMove);
    }, []);

    return (
        <Box
            ref={rootRef}
            aria-hidden="true"
            sx={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none', zIndex: 0, bgcolor: palette.canvas, '--mx': 0, '--my': 0 }}
        >
            <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `
                    radial-gradient(820px circle at 4% -8%,   ${alpha(palette.rust, 0.14)}, transparent 60%),
                    radial-gradient(680px circle at 96% 10%,  ${alpha(palette.pine, 0.11)}, transparent 58%),
                    radial-gradient(720px circle at 50% 110%, ${alpha(palette.gold, 0.1)}, transparent 60%),
                    radial-gradient(560px circle at 78% 64%,  ${alpha(palette.purple, 0.08)}, transparent 58%)
                `,
            }} />

            <Box
                component="svg"
                width="100%"
                height="100%"
                sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.14,
                    transform: 'translate3d(calc(var(--mx) * -14px), calc(var(--my) * -14px), 0)',
                    transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
                    maskImage: 'radial-gradient(ellipse 92% 76% at 50% 40%, #000 26%, transparent 78%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 92% 76% at 50% 40%, #000 26%, transparent 78%)',
                }}
            >
                <defs>
                    <pattern id="tfCoGrid" width="60" height="60" patternUnits="userSpaceOnUse">
                        <path d="M 60 0 L 0 0 0 60" fill="none" stroke={palette.rust} strokeWidth="0.5" />
                        <circle cx="0" cy="0" r="1.5" fill={palette.pine} />
                    </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#tfCoGrid)" />
            </Box>

            {CLOUDS.map((c, i) => (
                <Box key={i} sx={{
                    position: 'absolute',
                    top: c.top,
                    left: c.left,
                    width: c.size,
                    height: c.size,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(c.color, 0.2)} 0%, ${alpha(c.color, 0.07)} 42%, transparent 70%)`,
                    animation: `${c.anim} ${c.dur}s ease-in-out ${c.delay}s infinite`,
                    willChange: 'transform',
                }}>
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        transform: `translate3d(calc(var(--mx) * ${c.depth}px), calc(var(--my) * ${c.depth}px), 0)`,
                        transition: 'transform 0.5s cubic-bezier(0.16,1,0.3,1)',
                    }} />
                </Box>
            ))}

            <Box sx={{
                position: 'absolute', top: '-10%', right: '-16%',
                transform: 'translate3d(calc(var(--mx) * 30px), calc(var(--my) * 30px), 0)',
                transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
            }}>
                <BroadcastRings size={680} opacity={0.38} />
            </Box>
            <Box sx={{
                position: 'absolute', bottom: '-22%', left: '-18%',
                transform: 'translate3d(calc(var(--mx) * -22px), calc(var(--my) * -22px), 0)',
                transition: 'transform 0.6s cubic-bezier(0.16,1,0.3,1)',
            }}>
                <BroadcastRings size={580} opacity={0.26} reverse />
            </Box>

            {GLYPHS.map(({ Icon, x, y, size, dur, delay }, i) => (
                <Box key={i} sx={{
                    position: 'absolute', left: x, top: y, color: palette.rust, opacity: 0.09,
                    animation: `tfFloatIcon ${dur}s ease-in-out ${delay}s infinite`,
                    willChange: 'transform, opacity',
                }}>
                    <Icon sx={{ fontSize: size }} />
                </Box>
            ))}

            {NODES.map((n, i) => (
                <Box key={i} sx={{
                    position: 'absolute', left: n.l, top: n.t, width: n.s, height: n.s,
                    borderRadius: '50%', background: n.c, boxShadow: `0 0 12px ${n.c}`, opacity: 0,
                    animation: `tfNodeRise ${n.d}s linear ${n.delay}s infinite`,
                    willChange: 'transform, opacity',
                }} />
            ))}

            <Box sx={{
                position: 'absolute', left: 0, right: 0, top: 0, height: '26vh',
                background: `linear-gradient(180deg, transparent, ${alpha(palette.rust, 0.045)} 55%, transparent)`,
                animation: 'tfScanSweep 12s linear infinite',
                willChange: 'transform, opacity',
            }} />

            <Box sx={{
                position: 'absolute', inset: 0, opacity: 0.3, mixBlendMode: 'overlay',
                backgroundImage:
                    'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27100%27 height=%27100%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.8%27 numOctaves=%273%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.05%27/%3E%3C/svg%3E")',
                animation: 'tfGrain 9s steps(6) infinite',
                willChange: 'transform',
            }} />
            <Box sx={{
                position: 'absolute', inset: 0,
                background: `radial-gradient(ellipse 120% 92% at 50% 45%, transparent 54%, ${alpha(palette.ink, 0.08)} 100%)`,
            }} />
        </Box>
    );
});
AuroraBackdrop.displayName = 'AuroraBackdrop';

// ============================================================================
// SHARED BITS
// ============================================================================

const SectionRule = ({ color = palette.rust, width = 44 }) => (
    <Box sx={{
        width,
        height: 4,
        borderRadius: 999,
        background: `linear-gradient(90deg, ${color}, ${alpha(color, 0)})`,
        boxShadow: `0 2px 8px ${alpha(color, 0.35)}, inset 0 1px 0 rgba(255,255,255,0.7)`,
        mt: 0.85,
    }} />
);

const Reveal = ({ children, delay = 0 }) => (
    <motion.div
        initial={{ opacity: 0, y: 26, scale: 0.985 }}
        whileInView={{ opacity: 1, y: 0, scale: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
        style={{ height: '100%', width: '100%' }}
    >
        {children}
    </motion.div>
);

// ============================================================================
// STAT CARD WITH OVERLAY ICON
// ============================================================================

const PremiumStatCard = ({ title, value, icon, overlayIcon, color, gradient, delay, loading }) => {
    const nodeRef = useRef(null);
    const hasRun = useRef(false);

    useEffect(() => {
        const node = nodeRef.current;
        if (!node) return undefined;

        if (typeof value !== 'number' || value <= 0) {
            node.textContent = '0';
            return undefined;
        }
        if (hasRun.current) {
            node.textContent = value.toLocaleString();
            return undefined;
        }

        let raf = null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting || hasRun.current) return;
                hasRun.current = true;
                const start = performance.now();
                const tick = (now) => {
                    const t = Math.min(1, (now - start) / 900);
                    const eased = 1 - Math.pow(1 - t, 3);
                    if (nodeRef.current) nodeRef.current.textContent = Math.round(value * eased).toLocaleString();
                    if (t < 1) raf = requestAnimationFrame(tick);
                };
                raf = requestAnimationFrame(tick);
                observer.disconnect();
            },
            { threshold: 0.3 }
        );
        observer.observe(node);

        return () => {
            observer.disconnect();
            if (raf) cancelAnimationFrame(raf);
        };
    }, [value]);

    return (
        <Reveal delay={delay}>
            <motion.div whileHover={{ y: -4 }} transition={{ type: 'spring', stiffness: 400, damping: 17 }} style={{ height: 'auto' }}>
                <Paper
                    elevation={0}
                    sx={{
                        ...glassCard({ level: 2, radius: '14px', accent: color }),
                        height: 'auto',
                        minHeight: 108,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        transition: `transform 0.4s ${EASE}, box-shadow 0.4s ease`,
                        '&:hover': {
                            transform: 'translateY(-6px)',
                            ...glassLift(4, color),
                            '& .stat-glow': { opacity: 0.8, transform: 'scale(1.25)' },
                            '& .stat-pod': { transform: 'scale(1.08) rotate(-6deg)' },
                            '& .stat-sheen': { animation: `tfSheen 0.9s ${EASE}` },
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '8%',
                            right: '8%',
                            height: '2px',
                            borderRadius: '0 0 999px 999px',
                            background: `linear-gradient(90deg, transparent, ${alpha(color, 0.85)}, transparent)`,
                            pointerEvents: 'none',
                            zIndex: 2,
                        },
                    }}
                >
                    <Box className="stat-sheen" sx={{
                        position: 'absolute', top: 0, bottom: 0, left: 0, width: '38%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                        transform: 'translateX(-130%) skewX(-18deg)',
                        pointerEvents: 'none', zIndex: 2,
                    }} />

                    <Box className="stat-glow" sx={{
                        position: 'absolute', top: -70, right: -70, width: 180, height: 180, borderRadius: '50%',
                        background: `radial-gradient(circle, ${alpha(color, 0.3)}, transparent 70%)`,
                        opacity: 0.35, transition: `all 0.6s ${EASE}`, pointerEvents: 'none',
                    }} />
                    <Box sx={{
                        position: 'absolute', inset: 0, opacity: 0.05,
                        backgroundImage: `radial-gradient(${color} 0.5px, transparent 0.5px)`,
                        backgroundSize: '16px 16px',
                        maskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                        WebkitMaskImage: 'radial-gradient(ellipse at 70% 30%, black 30%, transparent 70%)',
                        pointerEvents: 'none',
                    }} />

                    {/* Background Watermark Icon */}
                    {overlayIcon && (
                        <Box
                            sx={{
                                position: 'absolute',
                                right: -8,
                                bottom: -12,
                                opacity: 0.07,
                                transform: 'rotate(-12deg) scale(1.2)',
                                pointerEvents: 'none',
                                '& svg': { fontSize: 110, color: color },
                            }}
                        >
                            {overlayIcon}
                        </Box>
                    )}

                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0, p: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                            <Box className="stat-pod" sx={glassPod(color, 46, '14px')}>
                                {React.cloneElement(icon, { sx: { fontSize: 21 } })}
                            </Box>
                            <Box sx={{ ...glassPill(palette.pine), px: 1.2, height: 22, fontSize: '0.52rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                <Box sx={{ position: 'relative', width: 6, height: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: palette.pine }} />
                                    <Box sx={{
                                        position: 'absolute', width: 14, height: 14, borderRadius: '50%',
                                        border: `1px solid ${palette.pine}`, opacity: 0,
                                        animation: 'tfSignalWave 2.4s ease-out infinite',
                                    }} />
                                </Box>
                                Live
                            </Box>
                        </Box>

                        {loading ? (
                            <Skeleton width="50%" height={32} sx={{ bgcolor: palette.hairline }} />
                        ) : (
                            <Typography sx={{
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontWeight: 700,
                                fontSize: 'clamp(1.9rem, 3.6vw, 2.4rem)',
                                lineHeight: 1.1,
                                letterSpacing: '-0.02em',
                                background: `linear-gradient(145deg, ${palette.ink}, ${color})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                display: 'inline-block',
                                mb: 0.25,
                            }}>
                                <span ref={nodeRef}>0</span>
                            </Typography>
                        )}

                        <Typography sx={{
                            color: palette.inkSoft, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: '0.08em', fontSize: '0.65rem',
                        }}>
                            {title}
                        </Typography>
                    </Box>
                </Paper>
            </motion.div>
        </Reveal>
    );
};

// ============================================================================
// PHONE COUNTRY SELECTOR POPOVER
// ============================================================================

const PhoneCountrySelector = ({ open, anchorEl, onClose, onSelect, selectedCountry }) => {
    const [search, setSearch] = useState('');

    const countries = [
        { code: 'US', name: 'United States', dialCode: '+1', flag: '🇺🇸' },
        { code: 'GB', name: 'United Kingdom', dialCode: '+44', flag: '🇬🇧' },
        { code: 'IN', name: 'India', dialCode: '+91', flag: '🇮🇳' },
        { code: 'AU', name: 'Australia', dialCode: '+61', flag: '🇦🇺' },
        { code: 'CA', name: 'Canada', dialCode: '+1', flag: '🇨🇦' },
        { code: 'DE', name: 'Germany', dialCode: '+49', flag: '🇩🇪' },
        { code: 'FR', name: 'France', dialCode: '+33', flag: '🇫🇷' },
        { code: 'IT', name: 'Italy', dialCode: '+39', flag: '🇮🇹' },
        { code: 'ES', name: 'Spain', dialCode: '+34', flag: '🇪🇸' },
        { code: 'BR', name: 'Brazil', dialCode: '+55', flag: '🇧🇷' },
        { code: 'MX', name: 'Mexico', dialCode: '+52', flag: '🇲🇽' },
        { code: 'JP', name: 'Japan', dialCode: '+81', flag: '🇯🇵' },
        { code: 'CN', name: 'China', dialCode: '+86', flag: '🇨🇳' },
        { code: 'SG', name: 'Singapore', dialCode: '+65', flag: '🇸🇬' },
        { code: 'AE', name: 'UAE', dialCode: '+971', flag: '🇦🇪' },
        { code: 'SA', name: 'Saudi Arabia', dialCode: '+966', flag: '🇸🇦' },
        { code: 'NG', name: 'Nigeria', dialCode: '+234', flag: '🇳🇬' },
        { code: 'ZA', name: 'South Africa', dialCode: '+27', flag: '🇿🇦' },
        { code: 'KE', name: 'Kenya', dialCode: '+254', flag: '🇰🇪' },
    ];

    const filteredCountries = countries.filter(c =>
        c.name.toLowerCase().includes(search.toLowerCase()) ||
        c.dialCode.includes(search)
    );

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{ paper: {
                sx: {
                    ...glassCard({ level: 3, radius: '16px' }),
                    width: 320,
                    maxHeight: 400,
                    overflow: 'hidden',
                }
            } }}
        >
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${palette.hairline}` }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search country..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{
                        ...glassWell('10px'),
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': { border: 'none' },
                            '& input': { fontSize: '0.85rem', py: 0.75 }
                        }
                    }}
                    slotProps={{ input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 16, color: palette.inkSoft }} />
                            </InputAdornment>
                        ),
                        endAdornment: search && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch('')}>
                                    <CloseIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                            </InputAdornment>
                        )
                    } }}
                />
            </Box>
            <Box sx={{ overflowY: 'auto', maxHeight: 320 }}>
                {filteredCountries.map((country) => (
                    <MenuItem
                        key={country.code}
                        onClick={() => { onSelect(country); onClose(); }}
                        sx={{
                            py: 1,
                            px: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            borderBottom: `1px solid ${palette.hairline}`,
                            '&:hover': { bgcolor: `${palette.rust}08` },
                            '&.Mui-selected': { bgcolor: `${palette.rust}12` }
                        }}
                    >
                        <Typography sx={{ fontSize: 20 }}>{country.flag}</Typography>
                        <Typography sx={{ flex: 1, fontSize: '0.85rem', fontWeight: selectedCountry?.code === country.code ? 600 : 400 }}>
                            {country.name}
                        </Typography>
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem' }}>
                            {country.dialCode}
                        </Typography>
                        {selectedCountry?.code === country.code && (
                            <CheckCircleIcon sx={{ color: palette.rust, fontSize: 16 }} />
                        )}
                    </MenuItem>
                ))}
                {filteredCountries.length === 0 && (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                        <Typography sx={{ color: palette.inkSoft, fontSize: '0.85rem' }}>
                            No countries found
                        </Typography>
                    </Box>
                )}
            </Box>
        </Popover>
    );
};

// ============================================================================
// MEMBER CARD WITH GLASS DESIGN
// ============================================================================

const MemberCard = ({ member, onEdit, onBlock, onDelete, onViewProfile, onApprove, onMarkSeatPaid, isCurrentUser, canApprove }) => {
    const getStatusColor = () => {
        if (!member.isApproved) return palette.gold;
        if (member.isBlocked) return palette.clay;
        if (member.isActive) return palette.pine;
        return palette.inkFaint;
    };

    const getInitials = () => {
        if (member.displayName) {
            const parts = member.displayName.split(' ');
            return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2);
        }
        if (member.firstName && member.lastName) {
            return (member.firstName[0] + member.lastName[0]).toUpperCase();
        }
        if (member.firstName) {
            return member.firstName[0].toUpperCase();
        }
        return 'U';
    };

    const colors = [palette.rust, palette.pine, palette.teal, palette.gold, palette.purple];
    const colorIndex = member.id?.length % colors.length || 0;
    const accentColor = colors[colorIndex];

    return (
        <Reveal delay={0.03}>
            <motion.div whileHover={{ y: -6 }} transition={{ type: 'spring', stiffness: 400, damping: 17 }} style={{ height: 'auto' }}>
                <Paper
                    elevation={0}
                    sx={{
                        ...glassCard({ level: 1, radius: '14px', accent: getStatusColor() }),
                        height: 'auto',
                        minHeight: 158,
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        transition: `transform 0.4s ${EASE}, box-shadow 0.4s ease`,
                        '&:hover': {
                            transform: 'translateY(-6px)',
                            ...glassLift(3, getStatusColor()),
                            '& .member-avatar': { transform: 'scale(1.05) rotate(-3deg)' },
                            '& .member-sheen': { animation: `tfSheen 0.95s ${EASE}` },
                        },
                    }}
                >
                    <Box className="member-sheen" sx={{
                        position: 'absolute', top: 0, bottom: 0, left: 0, width: '38%',
                        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                        transform: 'translateX(-130%) skewX(-18deg)',
                        pointerEvents: 'none', zIndex: 3,
                    }} />

                    <Box
                        sx={{
                            height: '3px',
                            background: `linear-gradient(90deg, ${getStatusColor()}, ${getStatusColor()}40, transparent)`,
                            flexShrink: 0,
                            position: 'relative',
                            zIndex: 2,
                        }}
                    />

                    {!member.isApproved && (
                        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                            <Chip
                                icon={<PendingIcon sx={{ fontSize: 12, color: palette.gold }} />}
                                label="Pending"
                                size="small"
                                sx={{
                                    ...glassPill(palette.gold, true),
                                    height: 22,
                                    fontSize: '0.5rem',
                                }}
                            />
                        </Box>
                    )}
                    {isCurrentUser && (
                        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 2 }}>
                            <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: 12, color: palette.rust }} />}
                                label="You"
                                size="small"
                                sx={{
                                    ...glassPill(palette.rust, true),
                                    height: 22,
                                    fontSize: '0.5rem',
                                }}
                            />
                        </Box>
                    )}

                    <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', flex: 1, p: 1.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                            <Avatar
                                className="member-avatar"
                                src={member.photoURL}
                                sx={{
                                    width: 52,
                                    height: 52,
                                    borderRadius: '14px',
                                    background: member.photoURL ? `url(${member.photoURL}) center/cover` :
                                        `linear-gradient(135deg, ${accentColor}, ${accentColor}80)`,
                                    color: '#fff',
                                    fontWeight: 700,
                                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                    fontSize: '1.1rem',
                                    boxShadow: `0 4px 16px ${alpha(accentColor, 0.15)}`,
                                    transition: 'transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
                                    border: `2px solid ${alpha(accentColor, 0.1)}`,
                                }}
                            >
                                {!member.photoURL && getInitials()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        color: palette.ink,
                                        fontSize: '0.95rem',
                                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                        cursor: 'pointer',
                                        '&:hover': { color: palette.rust }
                                    }}
                                    onClick={() => onViewProfile(member)}
                                >
                                    {member.displayName || `${member.firstName || ''} ${member.lastName || ''}`.trim() || 'Unnamed User'}
                                    {member.isOwner && (
                                        <Chip
                                            label="Owner"
                                            size="small"
                                            sx={{
                                                ml: 0.75,
                                                height: 16,
                                                fontSize: '0.45rem',
                                                fontWeight: 700,
                                                bgcolor: `${palette.rust}15`,
                                                color: palette.rust,
                                            }}
                                        />
                                    )}
                                </Typography>
                                <Typography variant="caption" sx={{ color: palette.inkFaint, fontSize: '0.7rem' }}>
                                    {member.email}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            {member.role && (
                                <Chip
                                    label={member.role.charAt(0).toUpperCase() + member.role.slice(1)}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        bgcolor: member.role === 'admin' ? `${palette.rust}12` :
                                            member.role === 'manager' ? `${palette.gold}12` :
                                            `${palette.teal}12`,
                                        color: member.role === 'admin' ? palette.rust :
                                            member.role === 'manager' ? palette.gold : palette.teal,
                                    }}
                                />
                            )}
                            {member.designation && (
                                <Chip
                                    label={member.designation}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        bgcolor: alpha(palette.ink, 0.04),
                                        color: palette.inkSoft,
                                        border: `1px solid ${palette.hairline}`,
                                    }}
                                />
                            )}
                            {member.department && (
                                <Chip
                                    label={member.department}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        bgcolor: `${palette.teal}10`,
                                        color: palette.teal,
                                        border: `1px solid ${alpha(palette.teal, 0.06)}`,
                                    }}
                                />
                            )}
                            {member.isApproved && (
                                <Chip
                                    icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                                    label={member.isBlocked ? 'Blocked' : 'Active'}
                                    size="small"
                                    sx={{
                                        height: 22,
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        bgcolor: member.isBlocked ? `${palette.clay}12` : `${palette.pine}12`,
                                        color: member.isBlocked ? palette.clay : palette.pine,
                                    }}
                                />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                            <Chip
                                icon={(member.emailVerified || member.isVerified || member.whatsappVerified) ? <VerifiedIcon sx={{ fontSize: 12 }} /> : <PendingIcon sx={{ fontSize: 12 }} />}
                                label={(member.emailVerified || member.isVerified || member.whatsappVerified) ? 'Account verified' : 'Verification pending'}
                                size="small"
                                sx={{
                                    height: 22,
                                    fontSize: '0.58rem',
                                    fontWeight: 700,
                                    bgcolor: (member.emailVerified || member.isVerified || member.whatsappVerified) ? `${palette.pine}12` : `${palette.gold}12`,
                                    color: (member.emailVerified || member.isVerified || member.whatsappVerified) ? palette.pine : palette.gold,
                                }}
                            />
                            {String(member.paymentStatus || '').startsWith('extra_seat') && (
                                <Chip
                                    icon={<PaymentIcon sx={{ fontSize: 12 }} />}
                                    label={member.paymentStatus === 'extra_seat_paid' ? 'Extra seat paid' : `${member.extraSeatCurrency || ''} ${Number(member.extraSeatAmount || 0).toFixed(2)} seat payment`}
                                    size="small"
                                    sx={{ height: 22, fontSize: '0.58rem', fontWeight: 700, bgcolor: member.paymentStatus === 'extra_seat_paid' ? `${palette.pine}12` : `${palette.rust}12`, color: member.paymentStatus === 'extra_seat_paid' ? palette.pine : palette.rust }}
                                />
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 0.5, mt: 'auto', pt: 1.5, borderTop: `1px solid ${palette.hairline}` }}>
                            <Box sx={{ display: 'flex', gap: 0.5, flex: 1 }}>
                                <Tooltip title="View Profile">
                                    <IconButton
                                        size="small"
                                        onClick={() => onViewProfile(member)}
                                        sx={{ ...glassPod(palette.teal, 30, '9px'), '&:hover': { transform: 'scale(1.1)' } }}
                                    >
                                        <VisibilityIcon sx={{ fontSize: 14 }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {!isCurrentUser && canApprove && (
                                    <>
                                        {['extra_seat_payment_pending','extra_seat_payment_submitted'].includes(member.paymentStatus) && (
                                            <Tooltip title="Mark additional seat payment paid">
                                                <IconButton size="small" onClick={() => onMarkSeatPaid(member)} sx={{ ...glassPod(palette.rust, 30, '9px'), '&:hover': { transform: 'scale(1.1)' } }}>
                                                    <PaymentIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {!member.isApproved && (
                                            <Tooltip title="Approve">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onApprove(member)}
                                                    sx={{ ...glassPod(palette.pine, 30, '9px'), '&:hover': { transform: 'scale(1.1)' } }}
                                                >
                                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => onEdit(member)}
                                                sx={{ ...glassPod(palette.gold, 30, '9px'), '&:hover': { transform: 'scale(1.1)' } }}
                                            >
                                                <EditIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={member.isBlocked ? 'Unblock' : 'Block'}>
                                            <IconButton
                                                size="small"
                                                onClick={() => onBlock(member)}
                                                sx={{
                                                    ...glassPod(member.isBlocked ? palette.pine : palette.clay, 30, '9px'),
                                                    '&:hover': { transform: 'scale(1.1)' }
                                                }}
                                            >
                                                {member.isBlocked ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <BlockIcon sx={{ fontSize: 14 }} />}
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Remove">
                                            <IconButton
                                                size="small"
                                                onClick={() => onDelete(member)}
                                                sx={{ ...glassPod(palette.clay, 30, '9px'), '&:hover': { transform: 'scale(1.1)' } }}
                                            >
                                                <DeleteIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </motion.div>
        </Reveal>
    );
};

// ============================================================================
// GLASS SKELETON
// ============================================================================

const GlassSkeleton = memo(({ h = 16, w = '100%', radius = '8px', mb = 0 }) => (
    <Box sx={{ ...glassWell(radius), height: h, width: w, mb, position: 'relative', overflow: 'hidden' }}>
        <Box sx={{
            position: 'absolute',
            inset: 0,
            width: '38%',
            background: `linear-gradient(90deg, transparent, ${alpha(palette.paper, 0.9)}, transparent)`,
            animation: 'tfSkeleton 1.5s ease-in-out infinite',
            willChange: 'transform',
        }} />
    </Box>
));
GlassSkeleton.displayName = 'GlassSkeleton';

const CardSkeleton = memo(() => (
    <Box sx={{ ...glassCard({ level: 1, radius: '14px' }), p: 2.5, minHeight: 240 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <GlassSkeleton h={52} w={52} radius="14px" />
            <Box sx={{ flex: 1 }}>
                <GlassSkeleton h={14} w="65%" mb={0.5} />
                <GlassSkeleton h={10} w="45%" />
            </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
            <GlassSkeleton h={22} w={60} radius="999px" />
            <GlassSkeleton h={22} w={50} radius="999px" />
            <GlassSkeleton h={22} w={40} radius="999px" />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 1.5, borderTop: `1px solid ${palette.hairline}` }}>
            <GlassSkeleton h={30} w="60%" radius="999px" />
            <GlassSkeleton h={30} w={30} radius="999px" />
            <GlassSkeleton h={30} w={30} radius="999px" />
        </Box>
    </Box>
));
CardSkeleton.displayName = 'CardSkeleton';

// ============================================================================
// MAIN COMPANY TEAM COMPONENT
// ============================================================================

const CompanyTeam = () => {
    const navigate = useNavigate();
    const { currentUser, userData, companyData, approveTeamMember, addDepartment, removeDepartment, addDesignation, removeDesignation } = useAuth();
    const [loading, setLoading] = useState(true);
    const [teamMembers, setTeamMembers] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(6);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({ role: '', status: '', department: '' });
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState('add');
    const [selectedMember, setSelectedMember] = useState(null);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dialCode: '+1',
        countryCode: 'US',
        designation: '',
        department: '',
        role: 'employee',
        status: 'active',
        password: '',
        confirmPassword: '',
        permissions: ['read']
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, userId: null });
    const [blockConfirm, setBlockConfirm] = useState({ open: false, userId: null, block: false });
    const [companyDataState, setCompanyDataState] = useState(null);
    const [seatInfo, setSeatInfo] = useState(null);
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        blocked: 0,
        pending: 0,
        admins: 0,
        managers: 0,
        employees: 0,
        owners: 0,
    });
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);
    const [newDepartment, setNewDepartment] = useState('');
    const [newDesignation, setNewDesignation] = useState('');
    const [openDepartmentDialog, setOpenDepartmentDialog] = useState(false);
    const [openDesignationDialog, setOpenDesignationDialog] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);
    const fileInputRef = useRef(null);
    const [profileDialogOpen, setProfileDialogOpen] = useState(false);
    const [selectedProfileMember, setSelectedProfileMember] = useState(null);
    const [phoneCountryAnchorEl, setPhoneCountryAnchorEl] = useState(null);

    injectAssets();

    const companyId = resolveCompanyId({ companyData, userData, currentUser });


    const isAdmin = useCallback(() => {
        if (!currentUser) return false;
        const currentUserData = teamMembers.find(m => m.id === currentUser.uid);
        if (currentUserData) {
            return currentUserData.role === 'admin' || currentUserData.isOwner === true;
        }
        if (companyDataState?.createdBy?.email === currentUser.email) {
            return true;
        }
        return false;
    }, [currentUser, teamMembers, companyDataState]);

    // ============================================
    // FETCH COMPANY DATA & TEAM MEMBERS
    // ============================================

    const fetchCompanyData = useCallback(async () => {
        try {
            if (!companyId) return null;
            const companyRef = doc(db, 'companies', companyId);
            const companyDoc = await getDoc(companyRef);
            if (companyDoc.exists()) {
                const data = companyDoc.data();
                setCompanyDataState(data);
                const depts = data.departments || ['Executive', 'Sales', 'Marketing', 'Engineering', 'Operations', 'Support', 'Finance', 'HR', 'Product', 'Design'];
                setDepartments(depts);
                const desigs = data.designations || ['CEO', 'CTO', 'COO', 'VP', 'Director', 'Manager', 'Senior', 'Lead', 'Developer', 'Analyst', 'Associate', 'Intern'];
                setDesignations(desigs);
                return data;
            }
            return null;
        } catch (error) {
            console.error('Error fetching company data:', error);
            return null;
        }
    }, [companyId]);

    const fetchTeamMembers = useCallback(async () => {
        setLoading(true);
        try {
            if (!companyId) {
                setLoading(false);
                return;
            }

            const company = await fetchCompanyData();
            try {
                const subscriptionStatus = await getCompanySeatStatus(companyId);
                setSeatInfo(subscriptionStatus?.seats || null);
            } catch (seatError) {
                console.warn('Unable to load subscription seat status:', seatError?.message || seatError);
            }

            if (!company) {
                toast.error('Company not found');
                setLoading(false);
                return;
            }

            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('companyId', '==', companyId));
            const querySnapshot = await getDocs(q);

            const membersList = [];

            // Check if current user exists and is the company creator/owner
            if (currentUser) {
                const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
                if (currentUserDoc.exists()) {
                    const userData = currentUserDoc.data();
                    const isCreator = userData.isOwner === true ||
                                     userData.createdBy === true ||
                                     userData.role === 'admin' ||
                                     (company.createdBy && company.createdBy.email === userData.email) ||
                                     (company.createdBy && company.createdBy.uid === currentUser.uid);

                    const companySubscriptionActive = ['active', 'trial'].includes(String(company.subscription?.status || company.subscriptionStatus || '').toLowerCase());
                    if (isCreator && !userData.isApproved && companySubscriptionActive) {
                        // Never self-promote authorization fields from a page load.
                        // Registration/admin approval is the authoritative path for
                        // role, ownership, approval and seat activation.
                        console.warn('Company owner profile is awaiting authoritative approval/repair; no client-side privilege escalation was attempted.');
                    }

                    const alreadyExists = membersList.some(m => m.id === currentUser.uid);
                    if (!alreadyExists) {
                        membersList.push({
                            id: currentUser.uid,
                            ...userData,
                            isCurrentUser: true,
                            isOwner: isCreator || userData.isOwner === true,
                            companyName: company.companyName
                        });
                    }
                } else {
                    // Do not manufacture an active owner client-side. Registration and seat
                    // allocation are authoritative backend operations.
                    console.warn('Current authenticated user has no TelcoFair user profile; waiting for registration/company workflow to create it.');
                }
            }

            querySnapshot.forEach((doc) => {
                const data = doc.data();
                const alreadyExists = membersList.some(m => m.id === doc.id);
                if (!alreadyExists) {
                    const isCreator = data.isOwner === true ||
                                     data.createdBy === true ||
                                     data.role === 'admin' ||
                                     (company.createdBy && company.createdBy.email === data.email);

                    membersList.push({
                        id: doc.id,
                        ...data,
                        isCurrentUser: doc.id === currentUser?.uid,
                        isOwner: isCreator || data.isOwner === true,
                        companyName: company.companyName
                    });
                }
            });

            setTeamMembers(membersList);
            setFilteredMembers(membersList);
            calculateStats(membersList);

        } catch (error) {
            console.error('Error fetching team members:', error);
            toast.error(friendlyError(error, 'Failed to load team members.'));
        } finally {
            setLoading(false);
        }
    }, [companyId, currentUser, fetchCompanyData]);

    const calculateStats = useCallback((membersList) => {
        const stats = {
            total: membersList.length,
            active: membersList.filter(u => (u.status === 'active' || u.isActive) && u.isApproved).length,
            blocked: membersList.filter(u => u.status === 'blocked' || u.isBlocked).length,
            pending: membersList.filter(u => !u.isApproved && !u.isOwner).length,
            admins: membersList.filter(u => u.role === 'admin' && u.isApproved).length,
            managers: membersList.filter(u => u.role === 'manager' && u.isApproved).length,
            employees: membersList.filter(u => (u.role === 'employee' || u.role === 'viewer') && u.isApproved).length,
            owners: membersList.filter(u => u.isOwner).length,
        };
        setStats(stats);
    }, []);

    // ============================================
    // SAVE TEAM MEMBER - MANUAL ADDITION WITH EMAIL VERIFICATION
    // ============================================

    const handleSaveMember = useCallback(async () => {
        try {
            if (!formData.email) {
                toast.error('Email is required');
                return;
            }
            if (dialogMode === 'add' && String(formData.password || '').length < 8) {
                toast.error('Temporary password must contain at least 8 characters.');
                return;
            }

            const company = await fetchCompanyData();
            if (!company) {
                toast.error('Company not found');
                return;
            }

            let photoURL = null;
            let photoAsset = null;
            if (selectedFile) {
                const resolvedCompanyId = company.id || company.companyId || companyId;
                photoAsset = await uploadFile({ companyId: resolvedCompanyId, file: selectedFile, kind: 'image', folder: 'profile-photos' });
                photoURL = photoAsset.url;
            }

            if (dialogMode === 'add') {
                const tempPassword = formData.password;
                const result = await createCompanyMember(companyId, {
                    password: tempPassword,
                    role: formData.role || 'employee',
                    permissions: formData.permissions || ['read'],
                    profile: {
                        email: formData.email,
                        displayName: `${formData.firstName} ${formData.lastName}`.trim(),
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phoneNumber: formData.phone || '',
                        dialCode: formData.dialCode || '+1',
                        countryCode: formData.countryCode || 'US',
                        designation: formData.designation || '',
                        department: formData.department || '',
                        status: formData.status || 'active',
                        photoURL: photoURL || null,
                        photoAsset: photoAsset || null,
                    },
                });
                toast.success(result?.paymentRequired ? `Team member created. An additional seat payment of ${result.extraSeatCurrency || ''} ${Number(result.extraSeatAmount || 0).toFixed(2)} is required before activation.` : `Team member added. A subscription seat has been allocated to ${formData.firstName || formData.email}.`);
                if (result?.verificationWarning) {
                    toast.error(result.verificationWarning, { duration: 6000 });
                } else if (result?.email) {
                    toast.success(`Verification instructions prepared for ${result.email}.`, { duration: 4500 });
                }
            } else {
                // Edit existing member through the guarded company-admin
                // service so role/permission changes cannot bypass validation.
                const updateData = {
                    displayName: `${formData.firstName} ${formData.lastName}`.trim(),
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    phoneNumber: formData.phone || '',
                    dialCode: formData.dialCode || '+1',
                    countryCode: formData.countryCode || 'US',
                    designation: formData.designation || '',
                    department: formData.department || '',
                    role: formData.role,
                    permissions: formData.permissions || ['read'],
                };

                if (photoURL) updateData.photoURL = photoURL;
                if (photoAsset) updateData.photoAsset = photoAsset;

                await updateCompanyMember(companyId, selectedMember.id, updateData);
                toast.success('Team member updated successfully!');
            }

            setOpenDialog(false);
            setSelectedFile(null);
            setPhotoPreview(null);
            fetchTeamMembers();
        } catch (error) {
            console.error('Error saving team member:', error);
            toast.error(friendlyError(error, 'Failed to save team member'));
        }
    }, [formData, dialogMode, selectedMember, selectedFile, companyId, fetchCompanyData, fetchTeamMembers]);

    // ============================================
    // EMAIL VERIFICATION ADMIN ACTIONS
    // ============================================

    // ============================================
    // HANDLE APPROVE
    // ============================================

    const handleApproveMember = useCallback(async (member) => {
        try {
            if (member.id === currentUser?.uid) {
                toast.error('You cannot approve yourself');
                return;
            }

            await approveTeamMember(member.id);

            const updatedMembers = teamMembers.map(m => {
                if (m.id === member.id) {
                    return { ...m, isApproved: true, isActive: true, status: 'active' };
                }
                return m;
            });

            setTeamMembers(updatedMembers);
            setFilteredMembers(updatedMembers);
            calculateStats(updatedMembers);

            toast.success(`${member.displayName || member.firstName || 'Team member'} has been approved! They can now log in.`);
        } catch (error) {
            console.error('Error approving member:', error);
            toast.error(friendlyError(error, 'Failed to approve member.'));
        }
    }, [currentUser, teamMembers, approveTeamMember, calculateStats]);

    const handleMarkSeatPaid = useCallback(async (member) => {
        try {
            await markExtraSeatPaymentPaid(companyId, member.id, true);
            toast.success(`Additional user-seat payment marked paid for ${member.displayName || member.email}.`);
            await fetchTeamMembers();
            const subscriptionStatus = await getCompanySeatStatus(companyId).catch(() => null);
            if (subscriptionStatus?.seats) setSeatInfo(subscriptionStatus.seats);
        } catch (error) {
            console.error('Extra seat payment review error:', error);
            toast.error(friendlyError(error, 'Unable to confirm the additional user-seat payment.'));
        }
    }, [companyId, fetchTeamMembers]);

    // ============================================
    // DELETE TEAM MEMBER
    // ============================================

    const handleDeleteMember = useCallback(async () => {
        try {
            if (deleteConfirm.userId === currentUser?.uid) {
                toast.error('You cannot remove yourself');
                setDeleteConfirm({ open: false, userId: null });
                return;
            }

            await removeCompanyMember(companyId, deleteConfirm.userId);
            toast.success('Team member removed and the subscription seat was released.');
            setDeleteConfirm({ open: false, userId: null });
            await fetchTeamMembers();
            const refreshedSeatStatus = await getCompanySeatStatus(companyId).catch(() => null);
            if (refreshedSeatStatus?.seats) setSeatInfo(refreshedSeatStatus.seats);
        } catch (error) {
            console.error('Error deleting member:', error);
            toast.error('Failed to remove team member');
        }
    }, [deleteConfirm.userId, currentUser, fetchTeamMembers, companyId]);

    // ============================================
    // BLOCK/UNBLOCK
    // ============================================

    const handleBlockMember = useCallback(async () => {
        try {
            if (blockConfirm.userId === currentUser?.uid) {
                toast.error('You cannot block yourself');
                setBlockConfirm({ open: false, userId: null, block: false });
                return;
            }

            await setCompanyMemberStatus(companyId, blockConfirm.userId, !blockConfirm.block);
            toast.success(`Team member ${blockConfirm.block ? 'blocked and seat released' : 'reactivated and seat allocated'} successfully!`);
            setBlockConfirm({ open: false, userId: null, block: false });
            await fetchTeamMembers();
            const refreshedSeatStatus = await getCompanySeatStatus(companyId).catch(() => null);
            if (refreshedSeatStatus?.seats) setSeatInfo(refreshedSeatStatus.seats);
        } catch (error) {
            console.error('Error blocking member:', error);
            toast.error('Failed to update status');
        }
    }, [blockConfirm, currentUser, fetchTeamMembers, companyId]);

    // ============================================
    // DEPARTMENT MANAGEMENT
    // ============================================

    const handleAddDepartment = useCallback(async () => {
        if (!newDepartment.trim()) {
            toast.error('Please enter a department name');
            return;
        }

        if (departments.includes(newDepartment.trim())) {
            toast.error('Department already exists');
            return;
        }

        try {
            await addDepartment(companyId, newDepartment.trim());
            setDepartments([...departments, newDepartment.trim()]);
            setNewDepartment('');
            toast.success(`Department "${newDepartment.trim()}" added!`);
            setOpenDepartmentDialog(false);
        } catch (error) {
            console.error('Error adding department:', error);
            toast.error('Failed to add department');
        }
    }, [newDepartment, departments, addDepartment, companyId]);

    const handleDeleteDepartment = useCallback(async (dept) => {
        try {
            await removeDepartment(companyId, dept);
            setDepartments(departments.filter(d => d !== dept));
            toast.success(`Department "${dept}" removed!`);
        } catch (error) {
            console.error('Error deleting department:', error);
            toast.error('Failed to remove department');
        }
    }, [departments, removeDepartment, companyId]);

    // ============================================
    // DESIGNATION MANAGEMENT
    // ============================================

    const handleAddDesignation = useCallback(async () => {
        if (!newDesignation.trim()) {
            toast.error('Please enter a designation');
            return;
        }

        if (designations.includes(newDesignation.trim())) {
            toast.error('Designation already exists');
            return;
        }

        try {
            await addDesignation(companyId, newDesignation.trim());
            setDesignations([...designations, newDesignation.trim()]);
            setNewDesignation('');
            toast.success(`Designation "${newDesignation.trim()}" added!`);
            setOpenDesignationDialog(false);
        } catch (error) {
            console.error('Error adding designation:', error);
            toast.error('Failed to add designation');
        }
    }, [newDesignation, designations, addDesignation, companyId]);

    const handleDeleteDesignation = useCallback(async (desig) => {
        try {
            await removeDesignation(companyId, desig);
            setDesignations(designations.filter(d => d !== desig));
            toast.success(`Designation "${desig}" removed!`);
        } catch (error) {
            console.error('Error deleting designation:', error);
            toast.error('Failed to remove designation');
        }
    }, [designations, removeDesignation, companyId]);

    // ============================================
    // SEARCH & FILTER
    // ============================================

    const handleSearch = useCallback((event) => {
        const term = event.target.value.toLowerCase();
        setSearchTerm(term);
        filterMembers(term, filters);
    }, [filters]);

    const handleFilterChange = useCallback((field, value) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
        filterMembers(searchTerm, newFilters);
    }, [filters, searchTerm]);

    const filterMembers = useCallback((term, filterOptions) => {
        let filtered = teamMembers;

        if (term) {
            filtered = filtered.filter(member =>
                (member.displayName || '').toLowerCase().includes(term) ||
                (member.firstName || '').toLowerCase().includes(term) ||
                (member.lastName || '').toLowerCase().includes(term) ||
                (member.email || '').toLowerCase().includes(term) ||
                (member.designation || '').toLowerCase().includes(term) ||
                (member.department || '').toLowerCase().includes(term)
            );
        }

        if (filterOptions.role) {
            filtered = filtered.filter(member => member.role === filterOptions.role);
        }

        if (filterOptions.status) {
            if (filterOptions.status === 'pending') {
                filtered = filtered.filter(member => !member.isApproved);
            } else if (filterOptions.status === 'active') {
                filtered = filtered.filter(member => member.status === 'active' && member.isApproved);
            } else if (filterOptions.status === 'blocked') {
                filtered = filtered.filter(member => member.status === 'blocked' || member.isBlocked);
            } else if (filterOptions.status === 'inactive') {
                filtered = filtered.filter(member => member.status === 'inactive');
            }
        }

        if (filterOptions.department) {
            filtered = filtered.filter(member => member.department === filterOptions.department);
        }

        setFilteredMembers(filtered);
        setPage(0);
    }, [teamMembers]);

    // ============================================
    // DIALOG HANDLERS
    // ============================================

    const handleOpenAddDialog = useCallback(() => {
        setDialogMode('add');
        setSelectedMember(null);
        setSelectedFile(null);
        setPhotoPreview(null);
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            dialCode: '+1',
            countryCode: 'US',
            designation: '',
            department: '',
            role: 'employee',
            status: 'active',
            password: '',
            confirmPassword: '',
            permissions: ['read']
        });
        setShowPassword(false);
        setShowConfirmPassword(false);
        setOpenDialog(true);
    }, []);

    const handleOpenEditDialog = useCallback((member) => {
        setDialogMode('edit');
        setSelectedMember(member);
        setSelectedFile(null);
        setPhotoPreview(member.photoURL || null);
        setFormData({
            firstName: member.firstName || '',
            lastName: member.lastName || '',
            email: member.email || '',
            phone: member.phoneNumber || '',
            dialCode: member.dialCode || '+1',
            countryCode: member.countryCode || 'US',
            designation: member.designation || '',
            department: member.department || '',
            role: member.role || 'employee',
            status: member.status || (member.isActive ? 'active' : 'inactive'),
            password: '',
            confirmPassword: '',
            permissions: member.permissions || ['read']
        });
        setShowPassword(false);
        setShowConfirmPassword(false);
        setOpenDialog(true);
    }, []);

    const handleViewProfile = useCallback((member) => {
        setSelectedProfileMember(member);
        setProfileDialogOpen(true);
    }, []);

    const handleSelectPhoneCountry = useCallback((country) => {
        setFormData(prev => ({
            ...prev,
            dialCode: country.dialCode,
            countryCode: country.code,
        }));
        setPhoneCountryAnchorEl(null);
    }, []);

    // ============================================
    // PAGINATION
    // ============================================

    const handleChangePage = useCallback((event, newPage) => {
        setPage(newPage);
    }, []);

    const handleChangeRowsPerPage = useCallback((event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    }, []);

    // ============================================
    // USE EFFECT
    // ============================================

    useEffect(() => {
        if (companyId) {
            fetchTeamMembers();
        } else {
            setLoading(false);
        }
    }, [companyId, currentUser, userData, companyData, fetchTeamMembers]);

    // ============================================
    // RENDER
    // ============================================

    const selectedCountry = { code: formData.countryCode, dialCode: formData.dialCode };

    const canvasSx = {
        minHeight: '100vh',
        position: 'relative',
        bgcolor: palette.canvas,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        '&::before': {
            content: '""',
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.5,
            backgroundImage:
                'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27120%27 height=%27120%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.9%27 numOctaves=%272%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27 opacity=%270.035%27/%3E%3C/svg%3E")',
        },
    };

    if (!companyId) {
        return (
            <Box className="erp-page-shell erp-internal-page tf-team-page" sx={canvasSx}>
                <style>{`${GLOBAL_CSS}`}</style>
                <Header />
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 'calc(100vh - 80px)', p: 3 }}>
                    <Alert
                        severity="info"
                        sx={{ maxWidth: 500, borderRadius: '16px', border: `1px solid ${palette.hairline}` }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => navigate('/dashboard/company')}
                                sx={{ fontWeight: 600, textTransform: 'none' }}
                            >
                                Go to Company Profile
                            </Button>
                        }
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", color: palette.ink }}>
                            No Company Found
                        </Typography>
                        <Typography variant="body2" sx={{ color: palette.inkSoft }}>
                            Please complete your company profile first to manage team members.
                        </Typography>
                    </Alert>
                </Box>
            </Box>
        );
    }

    if (loading) {
        return <LoadingSpinner label="Loading team workspace…" />;
    }

    const paginatedMembers = filteredMembers.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const statsData = [
        { title: 'Total Members', value: stats.total, icon: <PeopleIcon />, overlayIcon: <GroupIcon />, color: palette.rust, delay: 0.03 },
        { title: 'Active', value: stats.active, icon: <CheckCircleIcon />, overlayIcon: <CheckCircleIcon />, color: palette.pine, delay: 0.08 },
        { title: 'Pending', value: stats.pending, icon: <PendingIcon />, overlayIcon: <PendingIcon />, color: palette.gold, delay: 0.13 },
        { title: 'Admins', value: stats.admins, icon: <AdminIcon />, overlayIcon: <AdminIcon />, color: palette.purple, delay: 0.18 },
        { title: 'Blocked', value: stats.blocked, icon: <BlockIcon />, overlayIcon: <BlockIcon />, color: palette.clay, delay: 0.23 },
    ];

    const canApprove = isAdmin();

    return (
        <Box className="erp-page-shell erp-internal-page tf-team-page" sx={canvasSx}>
            <Header />

            <Container maxWidth={false} disableGutters sx={{
                py: { xs: 3, md: 4.5 },
                px: { xs: 2, sm: 3, md: 4, lg: 6 },
                position: 'relative',
                width: '100%',
                maxWidth: '1600px',
                mx: 'auto',
                zIndex: 1,
            }}>

                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <Box className="erp-page-hero" sx={{ ...glassCard({ level: 2, radius: '18px', accent: palette.rust }), p: { xs: 2, md: 2.25 }, mb: 2.5, overflow: 'hidden' }}>
                        <Box sx={{
                            position: 'absolute', top: 0, left: '6%', right: '6%', height: '2px',
                            borderRadius: '0 0 999px 999px',
                            background: `linear-gradient(90deg, transparent, ${alpha(palette.rust, 0.75)}, ${alpha(palette.gold, 0.75)}, ${alpha(palette.pine, 0.6)}, transparent)`,
                            backgroundSize: '300% 100%',
                            animation: 'tfEdgeShift 11s linear infinite',
                            pointerEvents: 'none',
                        }} />

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.25, minWidth: 0 }}>
                                <Box sx={{ position: 'relative', width: 60, height: 60, display: { xs: 'none', sm: 'flex' }, alignItems: 'center', justifyContent: 'center' }}>
                                    {[0, 1, 2].map((i) => (
                                        <Box key={i} sx={{
                                            position: 'absolute', width: 50, height: 50, borderRadius: '50%',
                                            border: `2px solid ${palette.rust}`, opacity: 0,
                                            animation: `tfSignalWave 3s cubic-bezier(0.2,0.6,0.3,1) ${i * 0.9}s infinite`,
                                        }} />
                                    ))}
                                    <Box sx={{ ...glassPod(palette.rust, 52, '17px'), animation: 'tfBreathe 4s ease-in-out infinite', zIndex: 1 }}>
                                        <PeopleIcon sx={{ fontSize: 25 }} />
                                    </Box>
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Typography sx={{
                                        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 700,
                                        fontSize: { xs: '1.35rem', md: '1.5rem' }, color: palette.ink,
                                        letterSpacing: '-0.02em', lineHeight: 1.15,
                                    }}>
                                        Team Management
                                    </Typography>
                                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.92rem', mt: 0.4 }}>
                                        Manage your company team members and their access
                                    </Typography>
                                    <SectionRule />
                                    {companyDataState && (
                                        <Chip
                                            icon={<BusinessIcon sx={{ fontSize: 14 }} />}
                                            label={companyDataState.companyName}
                                            sx={{
                                                mt: 1.5,
                                                ...glassPill(palette.rust),
                                                px: 1.3,
                                                height: 28,
                                                fontSize: '0.65rem',
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
                                {stats.pending > 0 && (
                                    <Box sx={{ ...glassPill(palette.gold, true), px: 1.3, height: 26, fontSize: '0.62rem' }}>
                                        <PendingIcon sx={{ fontSize: 12 }} />
                                        {stats.pending} Pending
                                    </Box>
                                )}
                                <Box sx={{ ...glassPill(palette.pine, true), px: 1.3, height: 26, fontSize: '0.62rem' }}>
                                    <LiveIcon sx={{ fontSize: 9 }} />
                                    {stats.active} Active
                                </Box>
                                {seatInfo && (
                                    <Box sx={{ ...glassPill(seatInfo.limit && seatInfo.committed >= seatInfo.limit ? palette.clay : palette.blue, true), px: 1.3, height: 26, fontSize: '0.62rem' }}>
                                        <GroupIcon sx={{ fontSize: 12 }} />
                                        {seatInfo.committed ?? (Number(seatInfo.used || 0) + Number(seatInfo.reserved || 0))}/{seatInfo.unlimited ? '∞' : seatInfo.limit} Seats
                                    </Box>
                                )}
                                {canApprove && (
                                    <Box sx={{ ...glassPill(palette.rust, true), px: 1.3, height: 26, fontSize: '0.62rem' }}>
                                        <AdminIcon sx={{ fontSize: 12 }} />
                                        Admin Access
                                    </Box>
                                )}
                                <Button
                                    startIcon={<RefreshIcon sx={{ animation: loading ? 'tfRingSpin 1.1s linear infinite' : 'none' }} />}
                                    onClick={fetchTeamMembers}
                                    disabled={loading}
                                    sx={{ ...glassGhostButton(palette.rust), px: 2.25, py: 1, fontSize: '0.8rem' }}
                                >
                                    Refresh
                                </Button>
                                {canApprove && (
                                    <>
                                        <Button
                                            startIcon={<CategoryIcon />}
                                            onClick={() => setOpenDepartmentDialog(true)}
                                            sx={{ ...glassGhostButton(palette.teal), px: 2.25, py: 1, fontSize: '0.8rem' }}
                                        >
                                            Departments
                                        </Button>
                                        <Button
                                            startIcon={<WorkIcon />}
                                            onClick={() => setOpenDesignationDialog(true)}
                                            sx={{ ...glassGhostButton(palette.gold), px: 2.25, py: 1, fontSize: '0.8rem' }}
                                        >
                                            Designations
                                        </Button>
                                        <Button
                                            startIcon={<PersonAddIcon />}
                                            onClick={handleOpenAddDialog}
                                            disabled={Boolean(seatInfo && !seatInfo.unlimited && seatInfo.limit > 0 && seatInfo.committed >= seatInfo.limit && seatInfo.allowExtraSeats === false)}
                                            title={seatInfo && !seatInfo.unlimited && seatInfo.limit > 0 && seatInfo.committed >= seatInfo.limit ? (seatInfo.allowExtraSeats === false ? 'This package does not allow additional user seats.' : `Included seats are full. The next member will use the configured additional-seat charge (${seatInfo.extraSeatCurrency || ''} ${Number(seatInfo.extraSeatPrice || 0).toFixed(2)}).`) : 'Add team member'}
                                            sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 2.5, py: 1, fontSize: '0.82rem' }}
                                        >
                                            {seatInfo && !seatInfo.unlimited && seatInfo.limit > 0 && seatInfo.committed >= seatInfo.limit && seatInfo.allowExtraSeats !== false ? 'Add Paid Seat' : 'Add Team Member'}
                                        </Button>
                                    </>
                                )}
                                {!canApprove && (
                                    <Typography variant="caption" sx={{ color: palette.inkSoft, fontStyle: 'italic' }}>
                                        You need admin privileges to manage team members
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>
                </motion.div>

                {/* Stats Cards with Overlay Icons */}
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr 1fr' },
                    gap: 2.5,
                    width: '100%',
                    mb: 4,
                }}>
                    {statsData.map((stat, index) => (
                        <PremiumStatCard key={stat.title} {...stat} delay={stat.delay} loading={loading} />
                    ))}
                </Box>

                {/* Search and Filters */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2, duration: 0.5 }}
                >
                    <Paper elevation={0} sx={{
                        ...glassCard({ level: 2, radius: '16px', accent: palette.rust }),
                        p: { xs: 2.5, md: 3 },
                        width: '100%',
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                        transition: `transform 0.4s ${EASE}, box-shadow 0.4s ease`,
                        '&:focus-within': { transform: 'translateY(-3px)', ...glassLift(3, palette.rust) },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0, left: '6%', right: '6%', height: '2px',
                            borderRadius: '0 0 999px 999px',
                            background: `linear-gradient(90deg, transparent, ${alpha(palette.rust, 0.6)}, ${alpha(palette.gold, 0.6)}, ${alpha(palette.pine, 0.6)}, ${alpha(palette.purple, 0.6)}, transparent)`,
                            backgroundSize: '300% 100%',
                            animation: 'tfEdgeShift 12s linear infinite',
                            pointerEvents: 'none',
                        },
                    }}>
                        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            <TextField
                                placeholder="Search team members..."
                                value={searchTerm}
                                onChange={handleSearch}
                                sx={{
                                    flex: '1 1 250px',
                                    minWidth: '200px',
                                    '& .MuiOutlinedInput-root': {
                                        ...glassWell('12px'),
                                        fontSize: '0.85rem',
                                        transition: `all 0.3s ${EASE}`,
                                        '& fieldset': { border: 'none' },
                                        '&.Mui-focused': {
                                            borderColor: alpha(palette.rust, 0.5),
                                            boxShadow: `inset 0 1px 6px rgba(${SHADOW},0.06), 0 0 0 3px ${alpha(palette.rust, 0.14)}`,
                                        },
                                        '& input': { py: 1.3, color: palette.ink },
                                    },
                                }}
                                slotProps={{ input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon sx={{ color: palette.inkFaint, fontSize: 20 }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => { setSearchTerm(''); filterMembers('', filters); }} aria-label="Clear search">
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                } }}
                            />
                            <FormControl size="small" sx={{ minWidth: '140px' }}>
                                <InputLabel sx={{ fontSize: '0.8rem' }}>Role</InputLabel>
                                <Select
                                    value={filters.role}
                                    onChange={(e) => handleFilterChange('role', e.target.value)}
                                    label="Role"
                                    sx={{
                                        ...glassWell('12px'),
                                        fontSize: '0.8rem',
                                        '& fieldset': { border: 'none' },
                                        '& .MuiSelect-select': { py: 1.2 },
                                    }}
                                    MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                                >
                                    <MenuItem value="">All Roles</MenuItem>
                                    <MenuItem value="admin">Administrator</MenuItem>
                                    <MenuItem value="manager">Manager</MenuItem>
                                    <MenuItem value="employee">Employee</MenuItem>
                                    <MenuItem value="viewer">Viewer</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: '140px' }}>
                                <InputLabel sx={{ fontSize: '0.8rem' }}>Status</InputLabel>
                                <Select
                                    value={filters.status}
                                    onChange={(e) => handleFilterChange('status', e.target.value)}
                                    label="Status"
                                    sx={{
                                        ...glassWell('12px'),
                                        fontSize: '0.8rem',
                                        '& fieldset': { border: 'none' },
                                        '& .MuiSelect-select': { py: 1.2 },
                                    }}
                                    MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="pending">Pending Approval</MenuItem>
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="blocked">Blocked</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: '140px' }}>
                                <InputLabel sx={{ fontSize: '0.8rem' }}>Department</InputLabel>
                                <Select
                                    value={filters.department}
                                    onChange={(e) => handleFilterChange('department', e.target.value)}
                                    label="Department"
                                    sx={{
                                        ...glassWell('12px'),
                                        fontSize: '0.8rem',
                                        '& fieldset': { border: 'none' },
                                        '& .MuiSelect-select': { py: 1.2 },
                                    }}
                                    MenuProps={{ slotProps: { paper: { sx: { ...glassCard({ level: 4, radius: '16px' }), mt: 1 } } } }}
                                >
                                    <MenuItem value="">All Departments</MenuItem>
                                    {departments.map((dept) => (
                                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {(filters.role || filters.status || filters.department || searchTerm) && (
                                <Button
                                    size="small"
                                    startIcon={<ClearIcon />}
                                    onClick={() => {
                                        setFilters({ role: '', status: '', department: '' });
                                        setSearchTerm('');
                                        setFilteredMembers(teamMembers);
                                    }}
                                    sx={{ ...glassGhostButton(palette.rust), px: 1.75, py: 0.8, fontSize: '0.75rem' }}
                                >
                                    Clear Filters
                                </Button>
                            )}
                            <Box sx={{ ml: 'auto' }}>
                                <Box sx={{ ...glassPill(palette.rust), px: 1.2, height: 26, fontSize: '0.65rem' }}>
                                    {filteredMembers.length} members
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </motion.div>

                {/* Team Members Grid */}
                <Box sx={{ mt: 4 }}>
                    {loading ? (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
                            gap: 3,
                            width: '100%',
                        }}>
                            {Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={`sk-${i}`} />)}
                        </Box>
                    ) : paginatedMembers.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Box sx={{ position: 'relative', width: 96, height: 96, mx: 'auto', mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {[0, 1].map((i) => (
                                    <Box key={i} sx={{
                                        position: 'absolute', width: 72, height: 72, borderRadius: '50%',
                                        border: `1.5px solid ${palette.inkFaint}`, opacity: 0,
                                        animation: `tfSignalWave 3.2s ease-out ${i * 1.1}s infinite`,
                                    }} />
                                ))}
                                <Box sx={{ ...glassPod(palette.inkFaint, 72, '50%'), opacity: 0.85 }}>
                                    <PeopleIcon sx={{ fontSize: 34 }} />
                                </Box>
                            </Box>
                            <Typography variant="h6" sx={{
                                color: palette.ink,
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontSize: '1.2rem'
                            }}>
                                {searchTerm || filters.role || filters.status || filters.department ? 'No members found' : 'No team members yet'}
                            </Typography>
                            <Typography variant="body2" sx={{ color: palette.inkFaint, fontSize: '0.9rem', mt: 1 }}>
                                {searchTerm || filters.role || filters.status || filters.department
                                    ? 'Try adjusting your search or filters'
                                    : 'Add your first team member to get started'}
                            </Typography>
                            {!searchTerm && !filters.role && !filters.status && !filters.department && canApprove && (
                                <Button
                                    onClick={handleOpenAddDialog}
                                    sx={{ ...glassButton(palette.rust, palette.rustDeep), mt: 3, px: 4, py: 1.2, fontSize: '0.85rem' }}
                                >
                                    <PersonAddIcon sx={{ mr: 1 }} />
                                    Add First Team Member
                                </Button>
                            )}
                            {!searchTerm && !filters.role && !filters.status && !filters.department && !canApprove && (
                                <Typography variant="body2" sx={{ color: palette.inkSoft, mt: 2 }}>
                                    Contact your company admin to add team members.
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr', lg: '1fr 1fr 1fr 1fr' },
                            gap: 3,
                            width: '100%',
                        }}>
                            {paginatedMembers.map((member) => (
                                <MemberCard
                                    key={member.id}
                                    member={member}
                                    onEdit={handleOpenEditDialog}
                                    onBlock={(m) => setBlockConfirm({ open: true, userId: m.id, block: !m.isBlocked })}
                                    onDelete={(m) => setDeleteConfirm({ open: true, userId: m.id })}
                                    onViewProfile={handleViewProfile}
                                    onApprove={handleApproveMember}
                                    onMarkSeatPaid={handleMarkSeatPaid}
                                    isCurrentUser={member.isCurrentUser}
                                    canApprove={canApprove}
                                />
                            ))}
                        </Box>
                    )}

                    {filteredMembers.length > rowsPerPage && (
                        <TablePagination
                            rowsPerPageOptions={[6, 12, 24, 48]}
                            component="div"
                            count={filteredMembers.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                mt: 2,
                                borderTop: `1px solid ${palette.hairline}`,
                                '& .MuiTablePagination-toolbar': { flexWrap: 'wrap' },
                                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    color: palette.inkSoft
                                }
                            }}
                        />
                    )}
                </Box>
            </Container>

            {/* ============================================
                ADD/EDIT TEAM MEMBER DIALOG
                ============================================ */}

            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '16px', accent: palette.rust }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{
                    borderBottom: `1px solid ${palette.hairline}`,
                    pb: 2,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                    fontWeight: 700,
                    color: palette.ink,
                }}>
                    <Box sx={glassPod(dialogMode === 'add' ? palette.rust : palette.gold, 40, '12px')}>
                        {dialogMode === 'add' ? <PersonAddIcon sx={{ fontSize: 20 }} /> : <EditIcon sx={{ fontSize: 20 }} />}
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                            {dialogMode === 'add' ? 'Add Team Member' : 'Edit Team Member'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                            {dialogMode === 'add' ? 'Invite a new team member to your company' : 'Update team member details'}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setOpenDialog(false)} sx={{ ml: 'auto', color: palette.inkSoft }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, px: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Profile Photo Upload */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3,
                            p: 2.5,
                            borderRadius: '16px',
                            ...glassWell('14px'),
                        }}>
                            <Box sx={{ position: 'relative' }}>
                                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                    <Box
                                        onClick={() => fileInputRef.current?.click()}
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            border: `3px dashed ${photoPreview ? palette.teal : palette.hairlineStrong}`,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            background: photoPreview ? `url(${photoPreview}) center/cover no-repeat` : 'rgba(255,255,255,0.6)',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s ease',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                borderColor: palette.teal,
                                                boxShadow: `0 8px 24px ${alpha(palette.teal, 0.15)}`
                                            }
                                        }}
                                    >
                                        {!photoPreview && (
                                            <>
                                                <PhotoCameraIcon sx={{ color: palette.teal, fontSize: 28, mb: 0.5 }} />
                                                <Typography sx={{ color: palette.inkSoft, fontSize: '0.6rem', fontWeight: 700, textAlign: 'center' }}>
                                                    Add Photo
                                                </Typography>
                                            </>
                                        )}
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    setSelectedFile(file);
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => setPhotoPreview(reader.result);
                                                    reader.readAsDataURL(file);
                                                }
                                            }}
                                            style={{ display: 'none' }}
                                        />
                                    </Box>
                                </motion.div>
                                {photoPreview && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            bottom: -4,
                                            right: -4,
                                            ...glassPod(palette.teal, 24, '50%'),
                                        }}
                                    >
                                        <CheckIcon sx={{ color: '#fff', fontSize: 14 }} />
                                    </Box>
                                )}
                            </Box>
                            <Box>
                                <Typography sx={{ fontWeight: 700, color: palette.ink, fontSize: '1rem' }}>
                                    Profile Photo
                                </Typography>
                                <Typography sx={{ color: palette.inkSoft, fontSize: '0.8rem', fontWeight: 500 }}>
                                    Upload a profile picture (optional)
                                </Typography>
                                {photoPreview && (
                                    <Button
                                        size="small"
                                        onClick={() => {
                                            setPhotoPreview(null);
                                            setSelectedFile(null);
                                            if (fileInputRef.current) {
                                                fileInputRef.current.value = '';
                                            }
                                        }}
                                        sx={{ ...glassGhostButton(palette.clay), fontSize: '0.75rem', mt: 0.5 }}
                                    >
                                        Remove Photo
                                    </Button>
                                )}
                            </Box>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="First Name *"
                                    value={formData.firstName}
                                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                                    required
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                            '& input': { py: 1.2 },
                                        }
                                    }}
                                    slotProps={{ input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonIcon sx={{ color: palette.teal }} />
                                            </InputAdornment>
                                        )
                                    } }}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    fullWidth
                                    label="Last Name *"
                                    value={formData.lastName}
                                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                                    required
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                            '& input': { py: 1.2 },
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>

                        <TextField
                            fullWidth
                            label="Email Address *"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            required
                            disabled={dialogMode === 'edit'}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('12px'),
                                    '& fieldset': { border: 'none' },
                                    '& input': { py: 1.2 },
                                }
                            }}
                            slotProps={{ input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon sx={{ color: palette.rust }} />
                                    </InputAdornment>
                                )
                            } }}
                        />

                        {/* Phone with Country Selector */}
                        <Box>
                            <Typography sx={{ fontWeight: 600, color: palette.inkSoft, fontSize: '0.88rem', mb: 1 }}>
                                Phone Number
                            </Typography>
                            <Grid container spacing={1.5}>
                                <Grid size={{ xs: 4 }}>
                                    <Button
                                        fullWidth
                                        onClick={(e) => setPhoneCountryAnchorEl(e.currentTarget)}
                                        sx={{
                                            ...pickerButtonSx(Boolean(selectedCountry)),
                                            justifyContent: 'center',
                                            gap: 0.5,
                                            px: 1,
                                        }}
                                    >
                                        {selectedCountry ? (
                                            <>
                                                <Typography sx={{ fontSize: 20 }}>{selectedCountry.flag || '🇺🇸'}</Typography>
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{formData.dialCode}</Typography>
                                            </>
                                        ) : (
                                            <>
                                                <PhoneIcon sx={{ color: palette.teal, fontSize: 18 }} />
                                                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>+1</Typography>
                                            </>
                                        )}
                                        <KeyboardArrowDownIcon sx={{ fontSize: 16, color: palette.inkSoft }} />
                                    </Button>
                                </Grid>
                                <Grid size={{ xs: 8 }}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="1234567890"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                ...glassWell('12px'),
                                                '& fieldset': { border: 'none' },
                                                '& input': { py: 1.2 },
                                            }
                                        }}
                                        slotProps={{ input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <PhoneIcon sx={{ color: palette.teal, fontSize: 18 }} />
                                                </InputAdornment>
                                            ),
                                        } }}
                                    />
                                </Grid>
                            </Grid>
                            <PhoneCountrySelector
                                open={Boolean(phoneCountryAnchorEl)}
                                anchorEl={phoneCountryAnchorEl}
                                onClose={() => setPhoneCountryAnchorEl(null)}
                                onSelect={handleSelectPhoneCountry}
                                selectedCountry={selectedCountry}
                            />
                        </Box>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Designation</InputLabel>
                                    <Select
                                        value={formData.designation}
                                        onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                                        label="Designation"
                                        sx={{
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                            '& .MuiSelect-select': { py: 1.2 },
                                        }}
                                    >
                                        <MenuItem value="">Select Designation</MenuItem>
                                        {designations.map((desig) => (
                                            <MenuItem key={desig} value={desig}>{desig}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Department</InputLabel>
                                    <Select
                                        value={formData.department}
                                        onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                                        label="Department"
                                        sx={{
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                            '& .MuiSelect-select': { py: 1.2 },
                                        }}
                                    >
                                        <MenuItem value="">Select Department</MenuItem>
                                        {departments.map((dept) => (
                                            <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Role</InputLabel>
                                    <Select
                                        value={formData.role}
                                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                        label="Role"
                                        sx={{
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                            '& .MuiSelect-select': { py: 1.2 },
                                        }}
                                    >
                                        <MenuItem value="admin">Administrator</MenuItem>
                                        <MenuItem value="manager">Manager</MenuItem>
                                        <MenuItem value="employee">Employee</MenuItem>
                                        <MenuItem value="viewer">Viewer</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <FormControl fullWidth>
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                        label="Status"
                                        sx={{
                                            ...glassWell('12px'),
                                            '& fieldset': { border: 'none' },
                                            '& .MuiSelect-select': { py: 1.2 },
                                        }}
                                    >
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="inactive">Inactive</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {dialogMode === 'add' && (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        helperText="Minimum 8 characters"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                ...glassWell('12px'),
                                                '& fieldset': { border: 'none' },
                                                '& input': { py: 1.2 },
                                            }
                                        }}
                                        slotProps={{ input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LockIcon sx={{ color: palette.gold }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Confirm Password"
                                        type={showConfirmPassword ? 'text' : 'password'}
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                ...glassWell('12px'),
                                                '& fieldset': { border: 'none' },
                                                '& input': { py: 1.2 },
                                            }
                                        }}
                                        slotProps={{ input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <VerifiedIcon sx={{ color: palette.gold }} />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                                                        {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        } }}
                                    />
                                </Grid>
                            </Grid>
                        )}

                        <Alert severity="info" sx={{ ...alertSx(palette.teal) }}>
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: palette.inkSoft }}>
                                <strong>Note:</strong> New members will receive a verification email and must be approved by an admin before they can log in.
                            </Typography>
                        </Alert>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.hairline}`, gap: 1.5 }}>
                    <Button onClick={() => setOpenDialog(false)} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSaveMember}
                        startIcon={dialogMode === 'add' ? <PersonAddIcon /> : <SaveIcon />}
                        sx={{ ...glassButton(palette.rust, palette.rustDeep), px: 3, py: 1 }}
                    >
                        {dialogMode === 'add' ? 'Add Member' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============================================
                DEPARTMENT MANAGEMENT DIALOG
                ============================================ */}

            <Dialog
                open={openDepartmentDialog}
                onClose={() => setOpenDepartmentDialog(false)}
                maxWidth="sm"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '16px', accent: palette.teal }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{
                    borderBottom: `1px solid ${palette.hairline}`,
                    pb: 2,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                    fontWeight: 700,
                    color: palette.ink,
                }}>
                    <Box sx={glassPod(palette.teal, 40, '12px')}>
                        <CategoryIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                            Manage Departments
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                            Add or remove departments in your company
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setOpenDepartmentDialog(false)} sx={{ ml: 'auto', color: palette.inkSoft }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, px: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                        <TextField
                            fullWidth
                            label="New Department Name"
                            value={newDepartment}
                            onChange={(e) => setNewDepartment(e.target.value)}
                            placeholder="Enter department name"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('12px'),
                                    '& fieldset': { border: 'none' },
                                    '& input': { py: 1.2 },
                                }
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddDepartment();
                                }
                            }}
                        />
                        <Button
                            onClick={handleAddDepartment}
                            startIcon={<AddIcon />}
                            sx={{ ...glassButton(palette.teal, palette.tealDeep), px: 3, py: 1 }}
                        >
                            Add
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {departments.map((dept) => (
                            <Chip
                                key={dept}
                                label={dept}
                                onDelete={() => handleDeleteDepartment(dept)}
                                sx={{
                                    ...glassPill(palette.teal),
                                    px: 1.2,
                                    height: 28,
                                    fontSize: '0.65rem',
                                }}
                                deleteIcon={<RemoveCircleIcon sx={{ color: palette.clay }} />}
                            />
                        ))}
                    </Box>
                    {departments.length === 0 && (
                        <Typography variant="body2" sx={{ color: palette.inkSoft, textAlign: 'center', py: 3 }}>
                            No departments added yet. Add your first department above.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* ============================================
                DESIGNATION MANAGEMENT DIALOG
                ============================================ */}

            <Dialog
                open={openDesignationDialog}
                onClose={() => setOpenDesignationDialog(false)}
                maxWidth="sm"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '16px', accent: palette.gold }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{
                    borderBottom: `1px solid ${palette.hairline}`,
                    pb: 2,
                    px: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                    fontWeight: 700,
                    color: palette.ink,
                }}>
                    <Box sx={glassPod(palette.gold, 40, '12px')}>
                        <WorkIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
                            Manage Designations
                        </Typography>
                        <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                            Add or remove designations in your company
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setOpenDesignationDialog(false)} sx={{ ml: 'auto', color: palette.inkSoft }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, px: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                        <TextField
                            fullWidth
                            label="New Designation Name"
                            value={newDesignation}
                            onChange={(e) => setNewDesignation(e.target.value)}
                            placeholder="Enter designation name"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    ...glassWell('12px'),
                                    '& fieldset': { border: 'none' },
                                    '& input': { py: 1.2 },
                                }
                            }}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddDesignation();
                                }
                            }}
                        />
                        <Button
                            onClick={handleAddDesignation}
                            startIcon={<AddIcon />}
                            sx={{ ...glassButton(palette.gold, palette.goldDeep), px: 3, py: 1 }}
                        >
                            Add
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {designations.map((desig) => (
                            <Chip
                                key={desig}
                                label={desig}
                                onDelete={() => handleDeleteDesignation(desig)}
                                sx={{
                                    ...glassPill(palette.gold),
                                    px: 1.2,
                                    height: 28,
                                    fontSize: '0.65rem',
                                }}
                                deleteIcon={<RemoveCircleIcon sx={{ color: palette.clay }} />}
                            />
                        ))}
                    </Box>
                    {designations.length === 0 && (
                        <Typography variant="body2" sx={{ color: palette.inkSoft, textAlign: 'center', py: 3 }}>
                            No designations added yet. Add your first designation above.
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* ============================================
                DELETE CONFIRMATION
                ============================================ */}

            <Dialog
                open={deleteConfirm.open}
                onClose={() => setDeleteConfirm({ open: false, userId: null })}
                maxWidth="xs"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '16px', accent: palette.clay }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{
                    borderBottom: `1px solid ${palette.hairline}`,
                    pb: 2,
                    px: 3,
                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                    fontWeight: 600,
                    color: palette.ink,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={glassPod(palette.clay, 40, '12px')}>
                            <DeleteIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem' }}>
                            Remove Team Member
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, px: 3 }}>
                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.95rem' }}>
                        Are you sure you want to remove this team member? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.hairline}`, gap: 1.5 }}>
                    <Button onClick={() => setDeleteConfirm({ open: false, userId: null })} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleDeleteMember} sx={{ ...glassButton(palette.clay, palette.clayLight), px: 3, py: 1 }}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============================================
                BLOCK CONFIRMATION
                ============================================ */}

            <Dialog
                open={blockConfirm.open}
                onClose={() => setBlockConfirm({ open: false, userId: null, block: false })}
                maxWidth="xs"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '16px', accent: blockConfirm.block ? palette.clay : palette.pine }), overflow: 'hidden' } } }) }}
            >
                <DialogTitle sx={{
                    borderBottom: `1px solid ${palette.hairline}`,
                    pb: 2,
                    px: 3,
                    fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                    fontWeight: 600,
                    color: palette.ink,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={glassPod(blockConfirm.block ? palette.clay : palette.pine, 40, '12px')}>
                            {blockConfirm.block ? <BlockIcon sx={{ fontSize: 20 }} /> : <CheckCircleIcon sx={{ fontSize: 20 }} />}
                        </Box>
                        <Typography sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", fontWeight: 600, fontSize: '1.1rem' }}>
                            {blockConfirm.block ? 'Block Team Member' : 'Unblock Team Member'}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3, px: 3 }}>
                    <Typography sx={{ color: palette.inkSoft, fontSize: '0.95rem' }}>
                        Are you sure you want to {blockConfirm.block ? 'block' : 'unblock'} this team member?
                        {blockConfirm.block && ' They will lose access to the platform.'}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${palette.hairline}`, gap: 1.5 }}>
                    <Button onClick={() => setBlockConfirm({ open: false, userId: null, block: false })} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                        Cancel
                    </Button>
                    <Button onClick={handleBlockMember} sx={{ ...glassButton(blockConfirm.block ? palette.clay : palette.pine, blockConfirm.block ? palette.clayLight : palette.pineLight), px: 3, py: 1 }}>
                        {blockConfirm.block ? 'Block' : 'Unblock'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ============================================
                MEMBER PROFILE DIALOG
                ============================================ */}

            <Dialog
                open={profileDialogOpen}
                onClose={() => setProfileDialogOpen(false)}
                maxWidth="md"
                fullWidth
                
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', backgroundColor: alpha(palette.ink, 0.3) } }, ...({ paper: { sx: { ...glassCard({ level: 4, radius: '16px', accent: palette.rust }), overflow: 'hidden', maxWidth: '720px' } } }) }}
            >
                {selectedProfileMember && (
                    <>
                        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: -100,
                                    right: -100,
                                    width: 300,
                                    height: 300,
                                    borderRadius: '50%',
                                    background: `radial-gradient(circle, ${palette.rust}15, transparent 70%)`,
                                    animation: 'tfFloatIcon 8s ease-in-out infinite',
                                }}
                            />
                            <DialogTitle sx={{
                                borderBottom: `1px solid ${palette.hairline}`,
                                pb: 2.5,
                                px: 3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                fontWeight: 700,
                                color: palette.ink,
                                background: palette.canvasAlt,
                                position: 'relative',
                                zIndex: 1,
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                    <motion.div
                                        whileHover={{ scale: 1.05, rotate: -5 }}
                                        transition={{ type: 'spring', stiffness: 400 }}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 64,
                                                height: 64,
                                                borderRadius: '16px',
                                                background: selectedProfileMember.photoURL ? `url(${selectedProfileMember.photoURL}) center/cover` : `linear-gradient(135deg, ${palette.rust}, ${palette.rustDeep})`,
                                                boxShadow: `0 8px 24px ${alpha(palette.rust, 0.3)}`,
                                                color: '#fff',
                                                fontWeight: 700,
                                                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                                                fontSize: '1.5rem',
                                            }}
                                        >
                                            {!selectedProfileMember.photoURL && (
                                                (selectedProfileMember.displayName?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)) ||
                                                (selectedProfileMember.firstName?.[0]?.toUpperCase() || 'U')
                                            )}
                                        </Avatar>
                                    </motion.div>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700} sx={{ fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif", color: palette.ink }}>
                                            {selectedProfileMember.displayName || `${selectedProfileMember.firstName || ''} ${selectedProfileMember.lastName || ''}`.trim() || 'Unnamed User'}
                                            {selectedProfileMember.isOwner && (
                                                <Chip
                                                    label="Owner"
                                                    size="small"
                                                    sx={{
                                                        ml: 0.75,
                                                        height: 18,
                                                        fontSize: '0.5rem',
                                                        fontWeight: 700,
                                                        bgcolor: `${palette.rust}15`,
                                                        color: palette.rust,
                                                    }}
                                                />
                                            )}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                {selectedProfileMember.email}
                                            </Typography>
                                            {selectedProfileMember.isCurrentUser && (
                                                <Chip
                                                    label="You"
                                                    size="small"
                                                    sx={{
                                                        ...glassPill(palette.rust),
                                                        height: 20,
                                                        fontSize: '0.55rem',
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                            <Chip
                                                label={selectedProfileMember.role?.charAt(0).toUpperCase() + selectedProfileMember.role?.slice(1) || 'Employee'}
                                                size="small"
                                                sx={{
                                                    height: 22,
                                                    fontSize: '0.6rem',
                                                    fontWeight: 600,
                                                    bgcolor: selectedProfileMember.role === 'admin' ? `${palette.rust}12` :
                                                        selectedProfileMember.role === 'manager' ? `${palette.gold}12` :
                                                        `${palette.teal}12`,
                                                    color: selectedProfileMember.role === 'admin' ? palette.rust :
                                                        selectedProfileMember.role === 'manager' ? palette.gold : palette.teal,
                                                }}
                                            />
                                            {selectedProfileMember.isApproved ? (
                                                <Chip
                                                    icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
                                                    label={selectedProfileMember.isBlocked ? 'Blocked' : 'Active'}
                                                    size="small"
                                                    sx={{
                                                        height: 22,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 600,
                                                        bgcolor: selectedProfileMember.isBlocked ? `${palette.clay}12` : `${palette.pine}12`,
                                                        color: selectedProfileMember.isBlocked ? palette.clay : palette.pine,
                                                    }}
                                                />
                                            ) : (
                                                <Chip
                                                    icon={<PendingIcon sx={{ fontSize: 12 }} />}
                                                    label="Pending"
                                                    size="small"
                                                    sx={{
                                                        height: 22,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 600,
                                                        bgcolor: `${palette.gold}12`,
                                                        color: palette.gold,
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    </Box>
                                </Box>
                                <IconButton onClick={() => setProfileDialogOpen(false)} sx={{ color: palette.inkSoft }}>
                                    <CloseIcon />
                                </IconButton>
                            </DialogTitle>
                        </Box>

                        <DialogContent sx={{ pt: 3, px: 3, pb: 1 }}>
                            <Grid container spacing={2.5}>
                                <Grid size={{ xs: 12 }}>
                                    <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '14px' }), p: 2.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <Box sx={{ ...glassPod(palette.rust, 40, '10px') }}>
                                                <AdminIcon sx={{ fontSize: 20 }} />
                                            </Box>
                                            <Box>
                                                <Typography variant="subtitle2" fontWeight={700} sx={{ color: palette.ink }}>
                                                    {selectedProfileMember.role === 'admin' ? 'Administrator' :
                                                     selectedProfileMember.role === 'manager' ? 'Manager' :
                                                     selectedProfileMember.role === 'employee' ? 'Employee' : 'Viewer'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                                    {selectedProfileMember.role === 'admin' ? 'Full access to all company settings' :
                                                     selectedProfileMember.role === 'manager' ? 'Can manage team members and view analytics' :
                                                     selectedProfileMember.role === 'employee' ? 'Standard access to company features' : 'Read-only access'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '12px' }), p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <EmailIcon sx={{ color: palette.rust, fontSize: 18 }} />
                                            <Box>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>Email</Typography>
                                                <Typography variant="body2" fontWeight={600}>{selectedProfileMember.email}</Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '12px' }), p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <PhoneIcon sx={{ color: palette.teal, fontSize: 18 }} />
                                            <Box>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>Phone</Typography>
                                                <Typography variant="body2" fontWeight={600}>
                                                    {selectedProfileMember.dialCode || ''} {selectedProfileMember.phoneNumber || 'N/A'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '12px' }), p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <WorkIcon sx={{ color: palette.gold, fontSize: 18 }} />
                                            <Box>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>Designation</Typography>
                                                <Typography variant="body2" fontWeight={600}>{selectedProfileMember.designation || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '12px' }), p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <CategoryIcon sx={{ color: palette.pine, fontSize: 18 }} />
                                            <Box>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>Department</Typography>
                                                <Typography variant="body2" fontWeight={600}>{selectedProfileMember.department || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>

                                <Grid size={{ xs: 12 }}>
                                    <Paper elevation={0} sx={{ ...glassCard({ level: 1, radius: '12px' }), p: 2 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <BusinessIcon sx={{ color: palette.purple, fontSize: 18 }} />
                                            <Box>
                                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>Company</Typography>
                                                <Typography variant="body2" fontWeight={600}>{selectedProfileMember.companyName || 'N/A'}</Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions sx={{
                            p: 3,
                            borderTop: `1px solid ${palette.hairline}`,
                            gap: 1.5,
                            flexWrap: 'wrap',
                            bgcolor: palette.canvasAlt,
                        }}>
                            <Button onClick={() => setProfileDialogOpen(false)} sx={{ ...glassGhostButton(palette.inkSoft), px: 2.5, py: 1 }}>
                                Close
                            </Button>
                            {!selectedProfileMember.isCurrentUser && canApprove && (
                                <>
                                    {!selectedProfileMember.isApproved && (
                                        <Button
                                            startIcon={<CheckCircleIcon />}
                                            onClick={() => {
                                                setProfileDialogOpen(false);
                                                handleApproveMember(selectedProfileMember);
                                            }}
                                            sx={{ ...glassButton(palette.pine, palette.pineLight), px: 3, py: 1 }}
                                        >
                                            Approve
                                        </Button>
                                    )}
                                    <Button
                                        startIcon={<EditIcon />}
                                        onClick={() => {
                                            setProfileDialogOpen(false);
                                            handleOpenEditDialog(selectedProfileMember);
                                        }}
                                        sx={{ ...glassGhostButton(palette.gold), px: 3, py: 1 }}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        startIcon={selectedProfileMember.isBlocked ? <CheckCircleIcon /> : <BlockIcon />}
                                        onClick={() => {
                                            setProfileDialogOpen(false);
                                            setBlockConfirm({ open: true, userId: selectedProfileMember.id, block: !selectedProfileMember.isBlocked });
                                        }}
                                        sx={{ ...glassGhostButton(selectedProfileMember.isBlocked ? palette.pine : palette.clay), px: 3, py: 1 }}
                                    >
                                        {selectedProfileMember.isBlocked ? 'Unblock' : 'Block'}
                                    </Button>
                                    <Button
                                        startIcon={<DeleteIcon />}
                                        onClick={() => {
                                            setProfileDialogOpen(false);
                                            setDeleteConfirm({ open: true, userId: selectedProfileMember.id });
                                        }}
                                        sx={{ ...glassButton(palette.clay, palette.clayLight), px: 3, py: 1 }}
                                    >
                                        Remove
                                    </Button>
                                </>
                            )}
                            {!canApprove && (
                                <Typography variant="caption" sx={{ color: palette.inkSoft }}>
                                    Only admins can manage team members
                                </Typography>
                            )}
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <style>{`
                @keyframes tfFloatIcon {
                    0%, 100% { transform: translate(0, 0) rotate(0deg); }
                    50% { transform: translate(-10px, -10px) rotate(5deg); }
                }
            `}</style>
        </Box>
    );
};

// Helper function for picker button
const pickerButtonSx = (active) => ({
    height: '54px',
    ...glassWell('12px', active ? palette.rust : null),
    display: 'flex',
    alignItems: 'center',
    px: 2,
    textTransform: 'none',
    color: palette.ink,
    fontWeight: 600,
    fontSize: '0.9rem',
    transition: `all 0.28s ${EASE}`,
    '&:hover': {
        borderColor: alpha(palette.rust, 0.4),
        transform: 'translateY(-2px)',
        boxShadow: `0 10px 24px rgba(${SHADOW},0.11), inset 0 1px 0 rgba(255,255,255,0.7)`,
    },
});

// Helper function for alert
const alertSx = (color) => ({
    borderRadius: '12px',
    bgcolor: alpha(color, 0.06),
    backdropFilter: 'blur(8px) saturate(160%)',
    WebkitBackdropFilter: 'blur(8px) saturate(160%)',
    border: `1px solid ${alpha(color, 0.2)}`,
    boxShadow: `0 4px 12px ${alpha(color, 0.06)}, inset 0 1px 0 rgba(255,255,255,0.6)`,
    color: palette.inkSoft,
    '& .MuiAlert-icon': { color },
    '& .MuiAlert-message': { fontSize: '0.78rem' },
});

export default CompanyTeam;